import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";

// Custom
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import DatePickerInput from "../../forms/input/DatePickerInput";

// React icons
import { FaRegTrashAlt } from "react-icons/fa";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { formatDate } from "../../utils/helper";
import { getTypeSpecificFields } from "../../utils/meterFieldsConfig";
import { getTariffsV2 } from "../../utils/tariffServiceV2";
import { MeterDeviceT, TariffResponseT, ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";
import CustomSwitch from "../switch/Switch";
import { getTypeSpecificConsumerFields } from "../../utils/consumerFieldsConfig";
import { renderTypeSpecificField } from "../../utils/formFieldRenderer";
import { useNavigate } from "react-router-dom";
import CustomSearch from "../search/Search";
import StatusModal from "./StatusModal";

const steps = ["Edit Consumer", "Edit Site", "Edit Contract", "Edit Meter"];

interface OptionType {
  value: string;
  label: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  options?: OptionType[];
  required: boolean;
}

declare global {
  interface Window {
    handleUpdateConsumerEdit?: (skipNext?: boolean) => void;
    handleSubmitSiteEdit?: (skipNext?: boolean) => void;
    handleSubmitContractEdit?: (skipNext?: boolean) => void;
    handleSaveMeterEdit?: (skipNext?: boolean) => void;
    debugConsumerAssetIds?: (consumerId?: string) => Promise<void>;
  }
}

interface EditConsumerModelProps {
  openModal: { type: string; id: string; onlyMeter: boolean };
  setOpenModal: (open: { type: string; id: string }) => void;
  setToast: (toast: ToastState) => void;
}

const EditConsumerModel = ({
  openModal,
  setOpenModal,
  setToast,
}: EditConsumerModelProps) => {
  const createOnlyMeter = openModal.onlyMeter || false;
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [consumerData, setConsumerData] = useState({});
  const [relatedData, setRelatedData] = useState<{
    sites: [];
    contracts: [];
    meters: MeterDeviceT[];
  }>({
    sites: [],
    contracts: [],
    meters: [],
  });

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error" | "warning";
    title: string;
    message: string;
    actions?: {
      primary?: { label: string; onClick: () => void };
      secondary?: { label: string; onClick: () => void };
      tertiary?: { label: string; onClick: () => void };
    };
  }>({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const showStatus = (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => {
    setStatusModal({
      open: true,
      type,
      title: title || type.charAt(0).toUpperCase() + type.slice(1),
      message,
      actions,
    });
  };

  // Load consumer data when modal opens
  const loadConsumerData = async () => {
    // console.log("🔄 loadConsumerData: Starting to load consumer data...");

    if (typeof openModal === "boolean" || !openModal?.id) {
      // console.log("❌ loadConsumerData: No consumer ID provided");
      return;
    }

    // console.log(`📋 loadConsumerData: Loading consumer ID: ${openModal.id}`);
    setIsLoading(true);

    try {
      // Load consumer data
      const consumerResponse = await fetchGETRequest(
        `customer/${openModal.id}`,
        {},
        true,
      );

      if (consumerResponse?.data) {
        // console.log("✅ loadConsumerData: Consumer data loaded successfully");
        // console.log(`📊 Consumer data:`, {
        //   id: consumerResponse.data.id?.id,
        //   name: consumerResponse.data.name,
        //   additionalInfo: consumerResponse.data.additionalInfo,
        // });

        setConsumerData(consumerResponse.data);

        // Extract asset IDs from consumer's additionalInfo
        const additionalInfo = consumerResponse.data.additionalInfo || {};
        const siteIds = additionalInfo.siteIds || [];
        const contractIds = additionalInfo.contractIds || [];
        const meterIds = additionalInfo.meterIds || [];

        // console.log(`📊 Asset IDs from consumer additionalInfo:`);
        // console.log(`   Sites: ${siteIds.length} - [${siteIds.join(", ")}]`);
        // console.log(
        //   `   Contracts: ${contractIds.length} - [${contractIds.join(", ")}]`
        // );
        // console.log(`   Meters: ${meterIds.length} - [${meterIds.join(", ")}]`);

        // Fetch assets using stored IDs if available, otherwise fetch from API relationships
        let sites = [];
        let contracts = [];
        let meters = [];

        // Fetch sites
        if (siteIds.length > 0) {
          // console.log(`🔍 Fetching sites from additionalInfo IDs...`);
          const sitesPromises = siteIds.map((siteId: string) =>
            fetchGETRequest(`asset/${siteId}`, {}, true),
          );
          const sitesResponses = await Promise.allSettled(sitesPromises);

          sites = sitesResponses
            .filter(
              (result) => result.status === "fulfilled" && result.value?.data,
            )
            .map((result: any) => result.value.data);
        } else {
          // console.log(
          //   `🔍 No site IDs in additionalInfo, fetching from API relationships...`
          // );
          // Fallback: fetch from customer/assets API
          const assetsResponse = await fetchGETRequest(
            `customer/${openModal.id}/assets?pageSize=100&page=0`,
            {},
            true,
          );
          if (assetsResponse?.data?.data) {
            sites = assetsResponse.data.data.filter(
              (asset: any) => asset.type?.toUpperCase() === "SITE",
            );
          }
        }

        // Fetch contracts
        if (contractIds.length > 0) {
          // console.log(`🔍 Fetching contracts from additionalInfo IDs...`);
          const contractsPromises = contractIds.map((contractId: string) =>
            fetchGETRequest(`asset/${contractId}`, {}, true),
          );
          const contractsResponses =
            await Promise.allSettled(contractsPromises);

          contracts = contractsResponses
            .filter(
              (result) => result.status === "fulfilled" && result.value?.data,
            )
            .map((result: any) => result.value.data);
        } else {
          // console.log(
          //   `🔍 No contract IDs in additionalInfo, fetching from API relationships...`
          // );
          // Fallback: fetch from customer/assets API
          const assetsResponse = await fetchGETRequest(
            `customer/${openModal.id}/assets?pageSize=100&page=0`,
            {},
            true,
          );
          if (assetsResponse?.data?.data) {
            contracts = assetsResponse.data.data.filter(
              (asset: any) => asset.type?.toUpperCase() === "CONTRACT",
            );
          }
        }

        // Fetch meters using customer/devices endpoint (this is always the primary source)
        // console.log(`🔍 Fetching meters from customer/devices API...`);
        const metersResponse = await fetchGETRequest(
          `customer/${openModal.id}/devices?pageSize=50&page=0`,
          {},
          true,
        );

        if (
          metersResponse?.data?.data &&
          Array.isArray(metersResponse.data.data)
        ) {
          meters = metersResponse.data.data;
        }

        // Set the data
        setRelatedData({
          sites,
          contracts,
          meters,
        });

        // console.log(`✅ loadConsumerData: Data loading completed successfully`);
        // console.log(`📊 Final loaded data:`);
        // console.log(`   Sites: ${sites.length} loaded`);
        // console.log(`   Contracts: ${contracts.length} loaded`);
        // console.log(`   Meters: ${meters.length} loaded`);

        // If we have assets but no IDs in additionalInfo, update the consumer
        if (
          (sites.length > 0 || contracts.length > 0 || meters.length > 0) &&
          siteIds.length === 0 &&
          contractIds.length === 0 &&
          meterIds.length === 0
        ) {
          // console.log(
          //   `🔄 loadConsumerData: Found assets but no IDs in additionalInfo, updating consumer...`
          // );

          const newSiteIds = sites.map((site: any) => site.id.id);
          const newContractIds = contracts.map(
            (contract: any) => contract.id.id,
          );
          const newMeterIds = meters.map((meter: any) => meter.id.id);

          // Update consumer additionalInfo with found asset IDs
          const updateData = {
            ...consumerResponse.data,
            additionalInfo: {
              ...additionalInfo,
              siteIds: newSiteIds,
              contractIds: newContractIds,
              meterIds: newMeterIds,
            },
          };

          try {
            await fetchPOSTRequest(`customer`, updateData, true);
            // console.log(
            //   `✅ loadConsumerData: Consumer additionalInfo updated with asset IDs`
            // );
          } catch (updateError) {
            console.warn(
              `⚠️ loadConsumerData: Failed to update consumer additionalInfo:`,
              updateError,
            );
          }
        }
      } else {
        console.error("❌ loadConsumerData: No consumer data received");
        setToast({
          message: "Failed to load consumer data",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ loadConsumerData: Error loading consumer data:", error);
      setToast({
        message: "Error loading consumer data",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      // console.log("🔄 loadConsumerData: Finished");
    }
  };

  // Load consumer data when modal opens
  const loadMeterData = async () => {
    // console.log("🔄 loadConsumerData: Starting to load consumer data...");

    if (typeof openModal === "boolean" || !openModal?.id) {
      // console.log("❌ loadConsumerData: No consumer ID provided");
      return;
    }

    // console.log(`📋 loadConsumerData: Loading consumer ID: ${openModal.id}`);
    setIsLoading(true);

    try {
      // Load consumer data
      const consumerResponse = await fetchGETRequest<MeterDeviceT>(
        `device/info/${openModal.id}`,
        {},
      );

      console.log("🔄 loadConsumerData: Consumer data loaded successfully");
      console.log(consumerResponse, openModal.id);

      setRelatedData({
        sites: [],
        contracts: [],
        meters: [consumerResponse],
      });
    } catch (error) {
      console.error("❌ loadConsumerData: Error loading meter data:", error);
      setToast({
        message: "Error loading meter data",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      // console.log("🔄 loadConsumerData: Finished");
    }
  };

  useEffect(() => {
    if (
      typeof openModal === "object" &&
      openModal?.type === "EDITCONSUMER" &&
      openModal?.id
    ) {
      if (createOnlyMeter) {
        loadMeterData();
        setActiveStep(3); // Reset to first step when opening
      } else {
        loadConsumerData();
        setActiveStep(0); // Reset to first step when opening
      }
    }
  }, [
    typeof openModal === "object" ? openModal?.type : null,
    typeof openModal === "object" ? openModal?.id : null,
  ]);

  useEffect(() => {
    if (createOnlyMeter) {
      setActiveStep(3); // Jump to last step
    }
  }, [createOnlyMeter]);

  const handleNext = () => {
    if (createOnlyMeter) return; // prevent moving forward
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (createOnlyMeter) return; // prevent going backward
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (index: number) => {
    if (createOnlyMeter && index !== 3) return; // prevent clicking non-meter steps
    setActiveStep(index);
  };

  const handleSave = (skipNext?: boolean) => {
    // Call the appropriate save function based on the current active step
    switch (activeStep) {
      case 0:
        if (typeof window.handleUpdateConsumerEdit === "function") {
          window.handleUpdateConsumerEdit(skipNext);
        }
        break;
      case 1:
        if (typeof window.handleSubmitSiteEdit === "function") {
          window.handleSubmitSiteEdit(skipNext);
        }
        break;
      case 2:
        if (typeof window.handleSubmitContractEdit === "function") {
          window.handleSubmitContractEdit(skipNext);
        }
        break;
      case 3:
        if (typeof window.handleSaveMeterEdit === "function") {
          window.handleSaveMeterEdit(skipNext);
        }
        break;
      default:
        console.log("Save clicked for step:", activeStep, steps[activeStep]);
        break;
    }
  };

  const handleDataUpdate = (stepIndex: number, updatedData: any) => {
    // Handle data updates from step components
    switch (stepIndex) {
      case 0:
        setConsumerData(updatedData);
        break;
      case 1:
        setRelatedData((prev) => ({ ...prev, sites: updatedData }));
        break;
      case 2:
        setRelatedData((prev) => ({ ...prev, contracts: updatedData }));
        break;
      case 3:
        setRelatedData((prev) => ({ ...prev, meters: updatedData }));
        break;
    }
  };

  // Function to refresh data after updates
  const refreshData = async () => {
    // console.log("🔄 refreshData: Starting data refresh");

    if (typeof openModal === "boolean" || !openModal?.id) {
      // console.log("❌ refreshData: No consumer ID available");
      return;
    }

    try {
      // console.log(
      //   "⏱️ refreshData: Adding delay to ensure database update is committed..."
      // );

      // Add a small delay to ensure the database update is committed
      await new Promise((resolve) => setTimeout(resolve, 500));

      // console.log("🔄 refreshData: Proceeding with data reload");
      if (createOnlyMeter) {
        await loadMeterData();
      } else {
        await loadConsumerData();
      }
      // console.log("✅ refreshData: Data refresh completed successfully");
    } catch (error) {
      console.error("❌ refreshData: Error during data refresh:", error);
      setToast({
        message: "Error refreshing data",
        severity: "error",
      });
    }
  };

  // Helper function to update consumer with asset IDs - PRESERVE EXISTING RELATIONSHIPS
  const updateConsumerWithAssetId = async (
    consumerId: string,
    assetType: string,
    assetId: string,
    action: "add" | "remove" = "add",
  ) => {
    // console.log(
    //   `🔄 updateConsumerWithAssetId: Starting for ${assetType} ID: ${assetId} action: ${action}`
    // );

    try {
      // Get current consumer data
      const consumerResponse = await fetchGETRequest(
        `customer/${consumerId}`,
        {},
        true,
      );

      if (!consumerResponse?.data) {
        throw new Error(`No consumer data received for ID: ${consumerId}`);
      }

      // Get current asset IDs from consumer additionalInfo
      const currentAdditionalInfo = consumerResponse.data.additionalInfo || {};
      const currentSiteIds = currentAdditionalInfo.siteIds || [];
      const currentContractIds = currentAdditionalInfo.contractIds || [];
      const currentMeterIds = currentAdditionalInfo.meterIds || [];

      // Preserve existing relationships and update only the specific asset type
      let updatedSiteIds = [...currentSiteIds];
      let updatedContractIds = [...currentContractIds];
      let updatedMeterIds = [...currentMeterIds];

      const assetTypeLower = assetType.toLowerCase();

      if (action === "add") {
        if (assetTypeLower === "site") {
          if (!updatedSiteIds.includes(assetId)) updatedSiteIds.push(assetId);
        } else if (assetTypeLower === "contract") {
          if (!updatedContractIds.includes(assetId))
            updatedContractIds.push(assetId);
        } else if (assetTypeLower === "meter") {
          if (!updatedMeterIds.includes(assetId)) updatedMeterIds.push(assetId);
        }
      } else if (action === "remove") {
        if (assetTypeLower === "site") {
          updatedSiteIds = updatedSiteIds.filter((id) => id !== assetId);
        } else if (assetTypeLower === "contract") {
          updatedContractIds = updatedContractIds.filter(
            (id) => id !== assetId,
          );
        } else if (assetTypeLower === "meter") {
          updatedMeterIds = updatedMeterIds.filter((id) => id !== assetId);
        }
      }

      // console.log(`📊 Updated asset relationships:`);
      // console.log(
      //   `   Sites: ${updatedSiteIds.length} - [${updatedSiteIds.join(", ")}]`
      // );
      // console.log(
      //   `   Contracts: ${
      //     updatedContractIds.length
      //   } - [${updatedContractIds.join(", ")}]`
      // );
      // console.log(
      //   `   Meters: ${updatedMeterIds.length} - [${updatedMeterIds.join(", ")}]`
      // );

      // Build clean update payload
      const updatePayload = {
        id: consumerResponse.data.id,
        tenantId: consumerResponse.data.tenantId,
        title: consumerResponse.data.title || "",
        name: consumerResponse.data.name || "",
        additionalInfo: {
          ...currentAdditionalInfo,
          siteIds: updatedSiteIds,
          contractIds: updatedContractIds,
          meterIds: updatedMeterIds,
        },
        // Include only non-null fields
        ...(consumerResponse.data.email && {
          email: consumerResponse.data.email,
        }),
        ...(consumerResponse.data.phone && {
          phone: consumerResponse.data.phone,
        }),
        ...(consumerResponse.data.country && {
          country: consumerResponse.data.country,
        }),
        ...(consumerResponse.data.state && {
          state: consumerResponse.data.state,
        }),
        ...(consumerResponse.data.city && { city: consumerResponse.data.city }),
        ...(consumerResponse.data.address && {
          address: consumerResponse.data.address,
        }),
        ...(consumerResponse.data.address2 && {
          address2: consumerResponse.data.address2,
        }),
        ...(consumerResponse.data.zip && { zip: consumerResponse.data.zip }),
        ...(consumerResponse.data.externalId && {
          externalId: consumerResponse.data.externalId,
        }),
      };

      // console.log(`📤 Updating consumer with preserved relationships...`);

      const updateResponse = await fetchPOSTRequest(
        `customer`,
        updatePayload,
        true,
      );

      if (updateResponse?.status === 200) {
        // console.log(`✅ Consumer relationships updated successfully`);
        // console.log(
        //   `📊 Total assets in consumer: Sites=${updatedSiteIds.length}, Contracts=${updatedContractIds.length}, Meters=${updatedMeterIds.length}`
        // );
      } else {
        console.error(`❌ Consumer update failed:`, updateResponse);
        throw new Error(
          `Consumer update failed with status: ${updateResponse?.status}`,
        );
      }
    } catch (error) {
      console.error(`❌ Error updating consumer with ${assetType} ID:`, error);
      throw error;
    }
  };

  // Debug helper function - available in browser console
  const debugConsumerAssetIds = async (consumerId?: string) => {
    const targetId =
      consumerId || (typeof openModal === "object" ? openModal?.id : null);
    if (!targetId) {
      // console.log("❌ No consumer ID provided");
      return;
    }

    try {
      // console.log(
      //   `🔍 Debug: Checking consumer ${targetId} asset relationships...`
      // );

      // Get consumer data
      const consumerResponse = await fetchGETRequest(
        `customer/${targetId}`,
        {},
        true,
      );

      if (consumerResponse?.data) {
        const additionalInfo = consumerResponse.data.additionalInfo || {};
        // console.log(`📊 Consumer additionalInfo:`, additionalInfo);

        const siteIds = additionalInfo.siteIds || [];
        const contractIds = additionalInfo.contractIds || [];
        const meterIds = additionalInfo.meterIds || [];

        // console.log(`📊 Asset IDs in additionalInfo:`);
        // console.log(`   Sites: ${siteIds.length} - [${siteIds.join(", ")}]`);
        // console.log(
        //   `   Contracts: ${contractIds.length} - [${contractIds.join(", ")}]`
        // );
        // console.log(`   Meters: ${meterIds.length} - [${meterIds.join(", ")}]`);

        // Check actual API relationships
        const [assetsResponse, devicesResponse] = await Promise.all([
          fetchGETRequest(
            `customer/${targetId}/assets?pageSize=100&page=0`,
            {},
            true,
          ),
          fetchGETRequest(
            `customer/${targetId}/devices?pageSize=100&page=0`,
            {},
            true,
          ),
        ]);

        // console.log(`📊 Actual API relationships:`);
        console.log(
          `   Assets API response:`,
          assetsResponse?.data?.data?.length || 0,
          "items",
        );
        console.log(
          `   Devices API response:`,
          devicesResponse?.data?.data?.length || 0,
          "items",
        );

        if (assetsResponse?.data?.data) {
          assetsResponse.data.data.forEach((asset: any, index: number) => {
            console.log(
              `   Asset ${index + 1}: ${asset.type} - ${asset.id.id}`,
            );
          });
        }

        if (devicesResponse?.data?.data) {
          devicesResponse.data.data.forEach((device: any, index: number) => {
            console.log(
              `   Device ${index + 1}: ${device.type} - ${device.id.id}`,
            );
          });
        }

        return {
          additionalInfo: additionalInfo,
          actualAssets: assetsResponse?.data?.data || [],
          actualDevices: devicesResponse?.data?.data || [],
        };
      }
    } catch (error) {
      console.error("❌ Debug error:", error);
    }
  };

  // // Make debug function available globally
  // useEffect(() => {
  //   window.debugConsumerAssetIds = debugConsumerAssetIds;
  //   return () => {
  //     delete window.debugConsumerAssetIds;
  //   };
  // }, [typeof openModal === "object" ? openModal?.id : null]);

  const RenderStepperView = useMemo(() => {
    switch (activeStep) {
      case 0:
        return (
          <EditConsumer
            consumerData={consumerData}
            setToast={setToast}
            showStatus={showStatus}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            onDataUpdate={(data: any) => handleDataUpdate(0, data)}
            refreshData={refreshData}
          />
        );
      case 1:
        return (
          <EditSite
            consumerData={consumerData}
            sitesData={relatedData.sites}
            setToast={setToast}
            showStatus={showStatus}
            createOnlyMeter={createOnlyMeter}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            onDataUpdate={(data: any) => handleDataUpdate(1, data)}
            refreshData={refreshData}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      case 2:
        return (
          <EditContract
            consumerData={consumerData}
            contractsData={relatedData.contracts}
            siteData={relatedData.sites}
            setToast={setToast}
            showStatus={showStatus}
            createOnlyMeter={createOnlyMeter}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            onDataUpdate={(data: any) => handleDataUpdate(2, data)}
            refreshData={refreshData}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      case 3:
        return (
          <EditMeter
            consumerData={consumerData}
            metersData={relatedData.meters}
            sitesData={relatedData.sites}
            setToast={setToast}
            showStatus={showStatus}
            createOnlyMeter={createOnlyMeter}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            onDataUpdate={(data: any) => handleDataUpdate(3, data)}
            refreshData={refreshData}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      default:
        return null;
    }
  }, [
    activeStep,
    consumerData,
    relatedData,
    setToast,
    refreshData,
    updateConsumerWithAssetId,
    showStatus,
  ]);

  if (isLoading) {
    return (
      <Dialog
        open={
          typeof openModal === "object" && openModal?.type === "EDITCONSUMER"
        }
        fullWidth
        maxWidth={"md"}
      >
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography>
              Loading {createOnlyMeter ? "Meter" : "consumer"} data...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      onClose={() => setOpenModal({ type: "", id: "" })}
      open={typeof openModal === "object" && openModal?.type === "EDITCONSUMER"}
      fullWidth
      maxWidth={"md"}
      sx={{
        zIndex: 999,
        "& .MuiDialog-paper": {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>{steps[activeStep]}</DialogTitle>
        <IconButton
          onClick={() => setOpenModal({ type: "", id: "" })}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Stepper */}
        {/* <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root.Mui-active": { color: "#1976d2" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#0396FF" },
                  "& .MuiStepLabel-root": {
                    cursor: "pointer",
                  },
                  "& .MuiStepLabel-label": {
                    cursor: "pointer",
                    textDecoration: "none",
                    color: activeStep === index ? "#1976d2" : "#666",
                    fontWeight: activeStep === index ? 600 : 400,
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#1976d2",
                    },
                  },
                }}
                onClick={() => handleStepClick(index)}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper> */}

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root.Mui-active": { color: "#1064ED" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#1064ED" },
                  gap: 0,
                  "& .MuiStepLabel-root": {
                    cursor:
                      createOnlyMeter && index !== 3
                        ? "not-allowed"
                        : "pointer",
                  },
                  "& .MuiStepLabel-label": {
                    cursor:
                      createOnlyMeter && index !== 3
                        ? "not-allowed"
                        : "pointer",
                    textDecoration: "none",
                    color:
                      activeStep === index
                        ? "#1064ED"
                        : createOnlyMeter && index !== 3
                          ? "#999"
                          : "#666",
                    fontWeight: activeStep === index ? 600 : 400,
                    fontSize: 15,
                    "&:hover": {
                      textDecoration:
                        createOnlyMeter && index !== 3 ? "none" : "underline",
                      color:
                        createOnlyMeter && index !== 3 ? "#999" : "#1064ED",
                    },
                  },
                }}
                onClick={() => handleStepClick(index)}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Consumer Info Display */}
        {!createOnlyMeter && consumerData && (
          <Box>
            <Stack
              sx={{
                p: 1,
                my: 1,
                maxWidth: "85%",
                mx: "auto",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Grid2 container={true} rowSpacing={1} columnSpacing={2}>
                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Consumer ID:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#e8f5e8",
                        color: "#2e7d32",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        border: "1px solid #4caf50",
                      }}
                    >
                      {(consumerData as any).id?.id}
                    </Typography>
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Sites:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor:
                          relatedData.sites.length > 0 ? "#e8f5e8" : "#f0f0f0",
                        color:
                          relatedData.sites.length > 0 ? "#2e7d32" : "#999",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        border:
                          relatedData.sites.length > 0
                            ? "1px solid #4caf50"
                            : "1px solid #ddd",
                      }}
                    >
                      {relatedData.sites.length}
                    </Typography>
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Contracts:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor:
                          relatedData.contracts.length > 0
                            ? "#e8f5e8"
                            : "#f0f0f0",
                        color:
                          relatedData.contracts.length > 0 ? "#2e7d32" : "#999",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        border:
                          relatedData.contracts.length > 0
                            ? "1px solid #4caf50"
                            : "1px solid #ddd",
                      }}
                    >
                      {relatedData.contracts.length}
                    </Typography>
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Meters:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor:
                          relatedData.meters.length > 0 ? "#e8f5e8" : "#f0f0f0",
                        color:
                          relatedData.meters.length > 0 ? "#2e7d32" : "#999",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        border:
                          relatedData.meters.length > 0
                            ? "1px solid #4caf50"
                            : "1px solid #ddd",
                      }}
                    >
                      {relatedData.meters.length}
                    </Typography>
                  </Box>
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: "auto", mt: 0 }}>{RenderStepperView}</Box>
      </DialogContent>

      {/* Global Footer for all steps */}
      <Divider />
      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          zIndex: 10,
          borderTop: "1px solid #e0e0e0",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          padding: "16px 24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            disabled={activeStep === 0}
            sx={{ textTransform: "none" }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <ContainedButton
            label={"Update"}
            onClick={handleSave}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
          />
          <Button
            variant="outlined"
            disabled={activeStep === steps.length - 1}
            sx={{ textTransform: "none" }}
            onClick={handleNext}
          >
            Next
          </Button>
        </Box>
      </DialogActions>
      {/* Status Modal */}
      <StatusModal
        open={statusModal.open}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
        type={statusModal.type}
        message={statusModal.message}
        title={statusModal.title}
        actions={statusModal.actions}
      />
    </Dialog>
  );
};

// EditConsumer Component
interface EditConsumerProps {
  consumerData: any;
  setToast: (toast: ToastState) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  activeStep: number;
  next: () => void;
  back: () => void;
  onDataUpdate: (data: any) => void;
  refreshData: () => Promise<void>;
}

const EditConsumer = ({
  consumerData,
  setToast,
  showStatus,
  activeStep,
  next,
  back,
  onDataUpdate,
  refreshData,
}: EditConsumerProps) => {
  const [activeTab, setActiveTab] = useState("Consumer Details");
  const [contactDetails, setContactDetails] = useState<any[]>([]);
  const [contactType, setContactType] = useState<any>(null);
  const [contactValue, setContactValue] = useState("");
  const [identityDetails, setIdentityDetails] = useState<any[]>([]);
  const [identityType, setIdentityType] = useState<any>(null);
  const [identityValue, setIdentityValue] = useState<string>("");
  const [createCustomer, setCreateCustomer] = useState<{
    id: { id: string; entityType: string };
    customerType: any;
    customerBillingType: any;
    salutation: any;
    firstName: string;
    lastName: string;
    customerCreationDate: Date;
    customerBirthDate: Date | null;
    consumerId: string;
    mobileNumber: string;
    emailId: string;
    billingFrequency: any;
    billingLevel: any;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    customerType: "",
    customerBillingType: "",
    salutation: "",
    firstName: "",
    lastName: "",
    customerCreationDate: new Date(),
    customerBirthDate: null,
    consumerId: "",
    mobileNumber: "",
    emailId: "",
    billingFrequency: "",
    billingLevel: "",
  });

  console.log("🔄 consumerData:", createCustomer);

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  // Load consumer data when consumerData changes
  useEffect(() => {
    if (consumerData?.id) {
      const additionalInfo = consumerData.additionalInfo || {};

      console.log("🔄 additionalInfo:", additionalInfo);

      setCreateCustomer({
        id: consumerData.id,
        customerType: additionalInfo.consumerType
          ? {
              value: additionalInfo.consumerType,
              label: additionalInfo.consumerType,
            }
          : "",
        customerBillingType: additionalInfo.consumerBillingType
          ? {
              value: additionalInfo.consumerBillingType,
              label: additionalInfo.consumerBillingType,
            }
          : "",
        salutation: additionalInfo.salutation
          ? {
              value: additionalInfo.salutation,
              label: additionalInfo.salutation,
            }
          : "",
        firstName: additionalInfo.firstName || "",
        lastName: additionalInfo.lastName || "",
        customerCreationDate: additionalInfo.createdOn
          ? new Date(additionalInfo.createdOn)
          : new Date(),
        customerBirthDate: additionalInfo.dob
          ? new Date(additionalInfo.dob)
          : null,
        consumerId: additionalInfo.consumerId || "",
        mobileNumber: consumerData.phone || "",
        emailId: consumerData.email || "",
        billingFrequency: additionalInfo.billingFrequency
          ? {
              value: additionalInfo.billingFrequency,
              label:
                additionalInfo.billingFrequency.charAt(0).toUpperCase() +
                additionalInfo.billingFrequency.slice(1),
            }
          : "",
        billingLevel: additionalInfo.billingLevel
          ? {
              value: additionalInfo.billingLevel,
              label:
                additionalInfo.billingLevel.charAt(0).toUpperCase() +
                additionalInfo.billingLevel.slice(1),
            }
          : "",
      });

      // Load contact details
      setContactDetails(additionalInfo.contacts || []);

      // Load identity details
      setIdentityDetails(additionalInfo.identity || []);
    }
  }, [consumerData]);

  const handleTabChange = (_: unknown, newValue: string) => {
    setActiveTab(newValue);
  };

  const addContact = () => {
    if (contactType && contactValue) {
      setContactDetails([
        ...contactDetails,
        { type: contactType?.value, value: contactValue },
      ]);
      setContactType(null);
      setContactValue("");
    } else {
      setToast({
        message: "Contact Type and Contact Value are required",
        severity: "error",
      });
    }
  };

  const addIdentity = () => {
    if (identityType && identityValue) {
      setIdentityDetails([
        ...identityDetails,
        { type: identityType?.value, value: identityValue },
      ]);
      setIdentityType(null);
      setIdentityValue("");
    } else {
      setToast({
        message: "Identity Type and Identity Value are required",
        severity: "error",
      });
    }
  };

  const removeContact = (index: number) => {
    setContactDetails(contactDetails.filter((_, i) => i !== index));
  };

  const removeIdentity = (index: number) => {
    setIdentityDetails(identityDetails.filter((_, i) => i !== index));
  };

  const handleSave = async (skipNext?: boolean) => {
    try {
      const requestData = {
        tenantId: {
          id: "6e191ce0-a3e2-11ee-9483-cd24c515eaca",
          entityType: "TENANT",
        },
        title: createCustomer.firstName + " " + createCustomer.lastName,
        additionalInfo: {
          salutation: createCustomer.salutation?.value || "",
          consumerType: createCustomer.customerType?.value || "",
          consumerBillingType: createCustomer.customerBillingType?.value || "",
          firstName: createCustomer.firstName || "",
          lastName: createCustomer.lastName || "",
          createdOn: createCustomer.customerCreationDate || "",
          dob: createCustomer.customerBirthDate || "",
          status: "active",
          contacts: contactDetails || [],
          identity: identityDetails || [],
          consumerId: createCustomer.consumerId,
          mobileNumber: createCustomer.mobileNumber,
          emailId: createCustomer.emailId,
          billingFrequency: createCustomer.billingFrequency?.value || "",
          billingLevel: createCustomer.billingLevel?.value || "",
        },
        id: { id: consumerData.id.id, entityType: "CUSTOMER" },
        email: createCustomer.emailId,
        phone: createCustomer.mobileNumber,
      };

      const response = await fetchPOSTRequest(`customer`, requestData, true);

      if (response?.status === 200) {
        showStatus("success", "Consumer updated successfully!");

        // Update parent component with new data
        if (onDataUpdate) {
          onDataUpdate(response.data);
        }

        if (!skipNext) {
          next();
        }
      } else {
        setToast({
          message: response?.data?.message || "Error updating consumer",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating consumer:", error);
      showStatus("error", "Error updating consumer");
    }
  };

  // Expose save function to global scope
  useEffect(() => {
    window.handleUpdateConsumerEdit = handleSave;
    return () => {
      delete window.handleUpdateConsumerEdit;
    };
  }, [createCustomer, contactDetails, identityDetails]);

  const validateEmail = (email: string) => {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!regex.test(email)) {
      setToast({ message: "Invalid email address", severity: "error" });
    }
  };

  const contactTypeOptions = [
    { value: "Phone", label: "Phone" },
    { value: "Email", label: "Email" },
    { value: "Address", label: "Address" },
  ];

  const identificationTypeOptions = [
    { value: "Aadhar", label: "Aadhar" },
    { value: "DL", label: "DL" },
    { value: "SSN", label: "SSN" },
  ];

  const TABS = [
    "Consumer Details",
    "Contact Details",
    "Identification Details",
  ];

  const dynamicTypeSpecificFields = useMemo(() => {
    return getTypeSpecificConsumerFields("consumer");
  }, []);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={createCustomer?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    createCustomer?.[item?.title?.toUpperCase()] || false
                  }
                  onChange={(event) => {
                    setCreateCustomer({
                      ...createCustomer,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="text"
            value={createCustomer?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Number":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="number"
            value={createCustomer?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Dropdown":
        return (
          <CustomOutlinedSelect
            label={item?.title}
            placeholder={item?.title}
            options={item?.options}
            value={createCustomer?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      default:
        return null;
    }
  };

  const TableContent = (
    data: { type: string; value: string }[],
    removeFunction: (index: number) => void,
  ) => {
    return (
      <TableContainer sx={{ mt: "20px" }}>
        <Table sx={{ border: "1px solid #D7DAE7" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: "10px", color: "black !important" }}
                className={"custom_table_thead_cell"}
              >
                Type
              </TableCell>
              <TableCell
                sx={{ color: "black !important" }}
                className={"custom_table_thead_cell"}
              >
                Value
              </TableCell>
              <TableCell
                sx={{ color: "black !important" }}
                className={"custom_table_thead_cell"}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index}>
                <TableCell sx={{ backgroundColor: "#D7DAE7" }}>
                  <Typography className={"custom_table_tbody_cell"}>
                    {item?.type}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className={"custom_table_tbody_cell"}>
                    {item?.value}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => removeFunction(index)}
                    sx={{ color: "#FF3B30" }}
                  >
                    <UnifiedIcon icon={FaRegTrashAlt} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Card sx={{ mt: "20px" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TabList
              onChange={handleTabChange}
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  backgroundColor: "##1280E7",
                  height: "3px",
                },
              }}
            >
              {TABS.map((item) => {
                return (
                  <Tab
                    key={item}
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                );
              })}
            </TabList>

            <ContainedButton
              label={"Create custom fields"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={() => toggleCustomFieldDrawer("Consumer", true)}
            />
          </Box>

          <CardContent>
            <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                {dynamicTypeSpecificFields.map((field) => (
                  <Grid
                    key={field.name}
                    size={{ xs: 12, md: 4, lg: 4 }}
                    mt={"10px"}
                  >
                    {renderTypeSpecificField(
                      field,
                      createCustomer,
                      setCreateCustomer,
                    )}
                  </Grid>
                ))}
              </Grid>

              {createdFields?.["Consumer"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Consumer"]?.map(
                      (item: any, index: number) => {
                        return (
                          <Grid
                            size={{ xs: 12, md: 4, lg: 4 }}
                            mt={"10px"}
                            key={index}
                          >
                            {getCustomFields(item)}
                          </Grid>
                        );
                      },
                    )}
                  </Grid>
                </>
              )}
            </TabPanel>

            <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack width={"75%"}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={contactTypeOptions}
                        label={"Contact Type"}
                        value={contactType}
                        onChange={(newValue) => {
                          setContactType(newValue);
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomInput
                        label={"Contact Value"}
                        placeholder={"Contact"}
                        value={contactValue}
                        onChange={(e) => {
                          setContactValue(e.target.value);
                          if (contactType?.value === "Email") {
                            validateEmail(e.target.value);
                          }
                        }}
                        type={
                          contactType?.value === "Email"
                            ? "email"
                            : contactType?.value === "Phone"
                              ? "number"
                              : contactType?.value === "Address"
                                ? "text"
                                : "text"
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                      <ContainedButton
                        label={"Add Contact"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          height: "35px",
                          color: "#ffffff",
                        }}
                        onClick={addContact}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {contactDetails && contactDetails?.length > 0 && (
                  <Stack width={"25%"}>
                    {TableContent(contactDetails, removeContact)}
                  </Stack>
                )}
              </Stack>
            </TabPanel>

            <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack width={"75%"}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={identificationTypeOptions}
                        label={"Identification Type"}
                        value={identityType}
                        onChange={(newValue) => {
                          setIdentityType(newValue);
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomInput
                        label={"Identification Value"}
                        placeholder={"Identification"}
                        value={identityValue}
                        onChange={(e) => {
                          setIdentityValue(e.target.value);
                        }}
                        type={
                          identityType?.value === "Aadhar" ? "number" : "text"
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                      <ContainedButton
                        label={"Add Identity"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          height: "35px",
                          color: "#ffffff",
                        }}
                        onClick={addIdentity}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {identityDetails && identityDetails?.length > 0 && (
                  <Stack width={"25%"}>
                    {TableContent(identityDetails, removeIdentity)}
                  </Stack>
                )}
              </Stack>
            </TabPanel>
          </CardContent>
        </TabContext>
      </Card>
    </Box>
  );
};

// EditSite Component
interface EditSiteProps {
  consumerData: any;
  sitesData: any;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  createOnlyMeter: boolean;
  activeStep: number;
  next: () => void;
  back: () => void;
  onDataUpdate: (data: any) => void;
  refreshData: () => Promise<void>;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => Promise<void>;
}

const EditSite = ({
  consumerData,
  sitesData,
  setToast,
  showStatus,
  createOnlyMeter,
  activeStep,
  next,
  back,
  onDataUpdate,
  refreshData,
  updateConsumerWithAssetId,
}: EditSiteProps) => {
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("Details");
  const [createNewSite, setCreateNewSite] = useState(false);
  const [siteData, setSiteData] = useState<{
    useAsLoadProfile: boolean;
    status: boolean;
    serviceCommodityType: any;
    serviceType: any;
    operatingHours: any;
    latitude: string;
    longitude: string;
    locationId: string;
    substationName: string;
    zoneNumber: string;
    feederName: string;
    dtrName: string;
    feederPillarNo: string;
    meterLocation: string;
    changeOfMeterLocation: string;
    dateOfConnection: Date;
    consumerType: any;
    houseNo: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    divisionCode: string;
    subDivisionCode: string;
    numberOfMeters: string;
    [key: string]: any;
  }>({
    useAsLoadProfile: false,
    status: true, // true = Active, false = Inactive
    serviceCommodityType: "",
    serviceType: "",
    operatingHours: "",
    latitude: "",
    longitude: "",
    locationId: "",
    substationName: "",
    zoneNumber: "",
    feederName: "",
    dtrName: "",
    feederPillarNo: "",
    meterLocation: "",
    changeOfMeterLocation: "",
    dateOfConnection: new Date(),
    // Location fields
    consumerType: "",
    houseNo: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    divisionCode: "",
    subDivisionCode: "",
    numberOfMeters: "",
  });
  const [activeMeters, setActiveMeters] = useState<string[]>([]);
  const [linkToCustomer, setLinkToCustomer] = useState("");

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const TABS = ["Details", "Coordinates", "Meters"];

  const commodityTypeOptions = [
    { label: "Electricity", value: "Electricity" },
    { label: "Water", value: "Water" },
    { label: "Gas", value: "Gas" },
  ];

  const serviceTypeOptions = [
    { label: "Commercial", value: "Commercial" },
    { label: "Residential", value: "Residential" },
  ];

  const consumerTypeOptions = [
    { label: "Personal", value: "Personal" },
    { label: "Business", value: "Business" },
  ];

  // Load site data when selected site changes
  useEffect(() => {
    if (selectedSite) {
      const additionalInfo = selectedSite.additionalInfo || {};

      setSiteData({
        id: selectedSite.id,
        useAsLoadProfile: additionalInfo.UseAsLoadProfile || false,
        status: additionalInfo.status === "Active" ? true : false, // Convert string to boolean
        serviceCommodityType: additionalInfo.serviceCommodityType
          ? {
              value: additionalInfo.serviceCommodityType,
              label: additionalInfo.serviceCommodityType,
            }
          : "",
        serviceType: additionalInfo.serviceType
          ? {
              value: additionalInfo.serviceType,
              label: additionalInfo.serviceType,
            }
          : "",
        operatingHours: additionalInfo.operatingHours || "",
        latitude: additionalInfo.latitude || "",
        longitude: additionalInfo.longitude || "",
        locationId: additionalInfo.locationId || "",
        substationName: additionalInfo.substationName || "",
        zoneNumber: additionalInfo.zoneNumber || "",
        feederName: additionalInfo.feederName || "",
        dtrName: additionalInfo.dtrName || "",
        feederPillarNo: additionalInfo.feederPillarNo || "",
        meterLocation: additionalInfo.meterLocation || "",
        changeOfMeterLocation: additionalInfo.changeOfMeterLocation || "",
        dateOfConnection: additionalInfo.dateOfConnection
          ? new Date(additionalInfo.dateOfConnection)
          : new Date(),
        // Location fields
        consumerType: additionalInfo.consumerType
          ? {
              value: additionalInfo.consumerType,
              label: additionalInfo.consumerType,
            }
          : "",
        houseNo: additionalInfo.houseNo || "",
        streetAddress: additionalInfo.streetAddress || "",
        city: additionalInfo.city || "",
        state: additionalInfo.state || "",
        zipCode: additionalInfo.zip || "",
        divisionCode: additionalInfo.divisionCode || "",
        subDivisionCode: additionalInfo.subDivisionCode || "",
        numberOfMeters: additionalInfo.numberOfMeters || "",
      });

      setActiveMeters(additionalInfo.activeMeters || []);
      setLinkToCustomer(consumerData?.id?.id || "");
    }
  }, [selectedSite, consumerData]);

  // Auto-select first site if available
  useEffect(() => {
    if (
      sitesData &&
      Array.isArray(sitesData) &&
      sitesData.length > 0 &&
      !selectedSite
    ) {
      setSelectedSite(sitesData[0]);
    }
  }, [sitesData]);

  const handleSave = async (skipNext?: boolean) => {
    if (!selectedSite) {
      setToast({
        message: "No site selected for editing",
        severity: "error",
      });
      return;
    }

    try {
      if (window.setIsLoading) window.setIsLoading(true);
      const requestData = {
        ...selectedSite,
        additionalInfo: {
          ...selectedSite.additionalInfo,
          UseAsLoadProfile: siteData.useAsLoadProfile,
          status: siteData.status ? "Active" : "Inactive",
          serviceCommodityType: siteData.serviceCommodityType?.value || "",
          serviceType: siteData.serviceType?.value || "",
          operatingHours: siteData.operatingHours,
          latitude: siteData.latitude,
          longitude: siteData.longitude,
          activeMeters: activeMeters,
          locationId: siteData.locationId,
          substationName: siteData.substationName,
          zoneNumber: siteData.zoneNumber,
          feederName: siteData.feederName,
          dtrName: siteData.dtrName,
          feederPillarNo: siteData.feederPillarNo,
          meterLocation: siteData.meterLocation,
          changeOfMeterLocation: siteData.changeOfMeterLocation,
          dateOfConnection: siteData.dateOfConnection?.toString(),
          consumerType: siteData.consumerType?.value || "",
          streetAddress: siteData.streetAddress,
          houseNo: siteData.houseNo,
          state: siteData.state,
          city: siteData.city,
          zip: siteData.zipCode,
          divisionCode: siteData.divisionCode,
          subDivisionCode: siteData.subDivisionCode,
          numberOfMeters: siteData.numberOfMeters,
        },
      };

      const response = await fetchPOSTRequest(`asset`, requestData, true);

      if (response?.status === 200) {
        if (!createOnlyMeter) {
          try {
            await updateConsumerWithAssetId(
              consumerData.id.id,
              "site",
              selectedSite.id.id,
            );
          } catch (updateError) {
            console.error(
              "❌ Error updating consumer with site ID:",
              updateError,
            );
            setToast({
              message:
                "Site updated but failed to update consumer relationship. Please refresh the page.",
              severity: "warning",
            });
          }
        }

        showStatus("success", "Site updated successfully!");
        if (!skipNext) next();
      } else {
        setToast({
          message: response?.data?.message || "Error updating site",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("❌ Error updating site:", error);
      showStatus("error", "Error updating site");
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  const handleCreateNewSite = async (skipNext?: boolean) => {
    if (!consumerData?.id?.id) {
      setToast({
        message: "Consumer ID is required.",
        severity: "error",
      });
      return;
    }

    if (window.setIsLoading) window.setIsLoading(true);

    try {
      const siteName = `Site${Math.floor(Math.random() * 1000000000)}`;
      const requestData = {
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: siteName,
        type: "SITE",
        label: "NY Building",
        additionalInfo: {
          UseAsLoadProfile: siteData.useAsLoadProfile,
          status: siteData.status ? "Active" : "Inactive",
          serviceCommodityType: siteData.serviceCommodityType?.value || "",
          serviceType: siteData.serviceType?.value || "",
          operatingHours: siteData.operatingHours,
          latitude: siteData.latitude,
          longitude: siteData.longitude,
          activeMeters: activeMeters,
          locationId: siteData.locationId,
          substationName: siteData.substationName,
          zoneNumber: siteData.zoneNumber,
          feederName: siteData.feederName,
          dtrName: siteData.dtrName,
          feederPillarNo: siteData.feederPillarNo,
          meterLocation: siteData.meterLocation,
          changeOfMeterLocation: siteData.changeOfMeterLocation,
          dateOfConnection: siteData.dateOfConnection?.toString(),
          consumerType: siteData.consumerType?.value || "",
          streetAddress: siteData.streetAddress,
          houseNo: siteData.houseNo,
          state: siteData.state,
          city: siteData.city,
          zip: siteData.zipCode,
          divisionCode: siteData.divisionCode,
          subDivisionCode: siteData.subDivisionCode,
          numberOfMeters: siteData.numberOfMeters,
        },
        customer_id: consumerData.id.id,
      };

      const response = await fetchPOSTRequest(`asset`, requestData);

      if (response?.id?.id) {
        const linkResponse = await fetchPOSTRequest(
          `customer/${consumerData.id.id}/asset/${response.id.id}`,
          {},
          true,
        );

        if (linkResponse?.status === 200) {
          await updateConsumerWithAssetId(
            consumerData.id.id,
            "site",
            response.id.id,
          );
          showStatus("success", "Site created and linked successfully!");
          await refreshData();
          if (!skipNext) next();
        } else {
          setToast({
            message: "Site created but failed to link to customer.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      console.error("❌ Error during site creation:", error);
      showStatus("error", "Error creating site");
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  const handleSubmit = (skipNext?: boolean) => {
    if (createNewSite || sitesData.length === 0) {
      handleCreateNewSite(skipNext);
    } else {
      handleSave(skipNext);
    }
  };

  // Expose save function to global scope
  useEffect(() => {
    window.handleSubmitSiteEdit = handleSubmit;
    return () => {
      delete window.handleSubmitSiteEdit;
    };
  }, [selectedSite, siteData, activeMeters, createNewSite, sitesData]);

  const addRow = () => {
    setActiveMeters((prevState) => [...prevState, ""]);
  };

  const removeRow = (index: number) => {
    const updatedMeters = activeMeters.filter((_, idx) => index !== idx);
    setActiveMeters([...updatedMeters]);
  };

  const handleMeterIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { value } = e.target;
    const updatedActiveMeters = [...activeMeters];
    updatedActiveMeters[index] = value;
    setActiveMeters(updatedActiveMeters);
  };

  const dynamicTypeSpecificFields = useMemo(() => {
    return getTypeSpecificConsumerFields("site");
  }, []);

  const dynamicTypeSpecificFieldsLocation = useMemo(() => {
    return getTypeSpecificConsumerFields("location");
  }, []);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={siteData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={siteData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setSiteData({
                      ...siteData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="text"
            value={siteData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Number":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="number"
            value={siteData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Dropdown":
        return (
          <CustomOutlinedSelect
            label={item?.title}
            placeholder={item?.title}
            options={item?.options}
            value={siteData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      default:
        return null;
    }
  };

  if (
    !sitesData ||
    !Array.isArray(sitesData) ||
    (sitesData.length === 0 && !createNewSite)
  ) {
    return (
      <Box sx={{ p: 4 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <ContainedButton
            label="Create Site"
            onClick={() => setCreateNewSite(true)}
          />
        </Stack>
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No sites found for this consumer
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Site Selection */}
      {!createNewSite && (
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" gutterBottom>
              Select Site to Edit
            </Typography>
            {(!sitesData || sitesData.length === 0) && (
              <ContainedButton
                label="Create New Site"
                onClick={() => {
                  setCreateNewSite(true);
                  setSelectedSite(null);
                  setSiteData({
                    useAsLoadProfile: false,
                    status: true,
                    serviceCommodityType: "",
                    serviceType: "",
                    operatingHours: "",
                    latitude: "",
                    longitude: "",
                    locationId: "",
                    substationName: "",
                    zoneNumber: "",
                    feederName: "",
                    dtrName: "",
                    feederPillarNo: "",
                    meterLocation: "",
                    changeOfMeterLocation: "",
                    dateOfConnection: new Date(),
                    consumerType: "",
                    houseNo: "",
                    streetAddress: "",
                    city: "",
                    state: "",
                    zipCode: "",
                    divisionCode: "",
                    subDivisionCode: "",
                    numberOfMeters: "",
                  });
                }}
              />
            )}
          </Stack>
          <Grid container spacing={2}>
            {sitesData.map((site) => (
              <Grid size={{ xs: 12, md: 6 }} key={site.id.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border:
                      selectedSite?.id.id === site.id.id
                        ? "2px solid #1976d2"
                        : "1px solid #e0e0e0",
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => setSelectedSite(site)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {site.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {site.id.id}
                    </Typography>
                    <Typography variant="body2">
                      Type: {site.additionalInfo?.serviceType || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      Address:{" "}
                      {`${site.additionalInfo?.houseNo ? site.additionalInfo?.houseNo + "," : ""} ${site.additionalInfo?.streetAddress ? site.additionalInfo?.streetAddress + "," : ""} ${site.additionalInfo?.city ? site.additionalInfo?.city + "," : ""} ${site.additionalInfo?.state ? site.additionalInfo?.state + "," : ""} ${site.additionalInfo?.zip ? site.additionalInfo?.zip : ""}` ||
                        "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Site Edit Form or Create Form */}
      {(selectedSite || createNewSite) && (
        <Card sx={{ mt: "20px" }}>
          {createNewSite && (
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">Create New Site</Typography>
              {sitesData.length > 0 && (
                <ContainedButton
                  label="Cancel"
                  onClick={() => {
                    setCreateNewSite(false);
                    if (sitesData.length > 0) setSelectedSite(sitesData[0]);
                  }}
                  sx={{ backgroundColor: "#FA6B6B" }}
                />
              )}
            </Box>
          )}
          <TabContext value={createNewSite ? TABS[0] : activeTab}>
            {!createNewSite && (
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "#CACDD7",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <TabList
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  scrollButtons={false}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "#1280E7",
                      height: "3px",
                    },
                  }}
                >
                  {TABS.map((item) => (
                    <Tab
                      key={item}
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  ))}
                </TabList>

                <ContainedButton
                  label={"Create custom fields"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={() => toggleCustomFieldDrawer("Site", true)}
                />
              </Box>
            )}

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Location Details
                </Typography>
                <Grid container spacing={2}>
                  {dynamicTypeSpecificFieldsLocation.map((field) => (
                    <Grid
                      key={field.name}
                      size={
                        field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                      }
                      mt={"10px"}
                    >
                      {renderTypeSpecificField(field, siteData, setSiteData)}
                    </Grid>
                  ))}
                </Grid>

                {/* Location Details Section */}
                <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                  <Divider sx={{ borderWidth: "1.5px" }} />
                </Stack>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Site Details
                </Typography>
                <Grid container spacing={2} mt={"10px"}>
                  {dynamicTypeSpecificFields.map((field) => (
                    <Grid
                      key={field.name}
                      size={
                        field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                      }
                      mt={"10px"}
                    >
                      {renderTypeSpecificField(field, siteData, setSiteData)}
                    </Grid>
                  ))}
                </Grid>

                {createdFields?.["Site"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Site"]?.map(
                        (item: any, index: number) => (
                          <Grid
                            size={{ xs: 12, md: 4, lg: 4 }}
                            mt={"10px"}
                            key={index}
                          >
                            {getCustomFields(item)}
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Latitude"}
                      placeholder={"Latitude"}
                      value={siteData?.latitude}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          latitude: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Longitude"}
                      placeholder={"Longitude"}
                      value={siteData?.longitude}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          longitude: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
                <Box display={"flex"} gap={"20px"}>
                  <Stack width={"100%"}>
                    {activeMeters?.map((item, index) => (
                      <Stack
                        key={index}
                        direction={"row"}
                        gap={"20px"}
                        alignItems={"center"}
                        sx={{ marginBottom: "16px" }}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          Meter ID:
                        </Typography>

                        <CustomInput
                          placeholder={"Meter ID"}
                          sx={{
                            width: "400px",
                            height: "40px",
                          }}
                          value={item}
                          onChange={(e) => handleMeterIdChange(e, index)}
                        />

                        <Box
                          sx={{
                            backgroundColor: "#FA6B6B",
                            width: "35px",
                            height: "35px",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => removeRow(index)}
                        >
                          <UnifiedIcon icon={FaRegTrashAlt} color="#ffffff" />
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                <ContainedButton
                  label={"Add Meter"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    mt: "20px",
                    color: "#ffffff",
                  }}
                  onClick={addRow}
                />
              </TabPanel>
            </CardContent>
          </TabContext>
        </Card>
      )}
    </Box>
  );
};

interface EditContractProps {
  consumerData: any;
  contractsData: any;
  siteData: any;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  createOnlyMeter: boolean;
  activeStep: number;
  next: () => void;
  back: () => void;
  onDataUpdate: (data: any) => void;
  refreshData: () => Promise<void>;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => Promise<void>;
}

const EditContract = ({
  consumerData,
  contractsData,
  siteData,
  setToast,
  showStatus,
  createOnlyMeter,
  activeStep,
  next,
  back,
  onDataUpdate,
  refreshData,
  updateConsumerWithAssetId,
}: EditContractProps) => {
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("Contract Details");
  // const [tariffsData, setTariffsData] = useState<
  //   {
  //     label: string;
  //     value: string | number;
  //   }[]
  // >([]);

  const [createNewOne, setCreateNewOne] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState<{
    registerRuleBook: {
      label: string;
      value: string;
    }[];
    subscribedTariff: {
      label: string;
      value: string;
    }[];
  }>({
    registerRuleBook: [
      {
        label: "TEST_RULE_BOOK2",
        value: "044cb970-133b-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "348958b0-12bd-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "64bcc530-12b8-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "c3967710-12c5-11ef-a093-1127a7ee82a3",
      },
    ],
    subscribedTariff: [],
  });
  const [names, setNames] = useState<string[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<any[]>([]);
  const [contractData, setContractData] = useState<{
    id: { id: string; entityType: string };
    consumerId: string;
    siteId: string;
    status: boolean;
    startDate: Date;
    endDate: Date | null;
    commodityType: any;
    commodityCategory: any;
    registerRuleBook: any;
    subscribedVoltage: string;
    subscribedTariff: any;
    billingCycle: string;
    producerPremise: string;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    consumerId: "",
    siteId: "",
    status: true, // true = Active, false = Inactive
    startDate: new Date(),
    endDate: null,
    commodityType: "",
    commodityCategory: "",
    registerRuleBook: "",
    subscribedVoltage: "",
    subscribedTariff: "",
    billingCycle: "",
    producerPremise: "",
  });
  const [billingData, setBillingData] = useState<{
    fromDate: Date;
    toDate: Date;
    billingStatus: string;
  }>({
    fromDate: new Date(),
    toDate: new Date(),
    billingStatus: "",
  });

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();
  const navigate = useNavigate();
  const TABS = ["Contract Details", "Billing Determinants"];

  const determinantStatusOptions = [
    { value: "Initial", label: "Initial" },
    { value: "Success", label: "Success" },
    { value: "Rejected", label: "Rejected" },
  ];

  const commodityTypeOptions = [
    { value: "Electricity", label: "Electricity" },
    { value: "Water", label: "Water" },
    { value: "Gas", label: "Gas" },
  ];

  const commodityCategoryOptions = [
    { value: "Residential", label: "Residential" },
    { value: "Commercial", label: "Commercial" },
  ];

  const mapAdditionalInfoToContractData = (
    contract: typeof selectedContract,
    dropdownOpt: typeof dropdownOptions,
  ) => {
    const info = contract.additionalInfo || {};

    const getDropdownValue = (
      value: string,
      options: { value: string; label: string }[],
    ) => options.find((item) => item.value === value) || "";

    return {
      id: contract.id,
      consumerId: info.consumerid || "",
      siteId: info.siteId || "",
      status: info.status === "Active",
      startDate: info.startdate ? new Date(info.startdate) : new Date(),
      endDate: info.enddate ? new Date(info.enddate) : null,
      commodityType: info.commodityType
        ? { value: info.commodityType, label: info.commodityType }
        : "",
      commodityCategory: info.commodityCategory
        ? { value: info.commodityCategory, label: info.commodityCategory }
        : "",
      registerRuleBook: info.registerRuleBook
        ? getDropdownValue(info.registerRuleBook, dropdownOpt.registerRuleBook)
        : "",
      subscribedVoltage: info.subscribedVoltage || "",
      subscribedTariff: info.subscribedTariff || "",
      billingCycle: info.billingCycle || "",
      producerPremise: info.producerPremise || "",
      tariffForm: info.tariffForm || "",
    };
  };

  // Load contract data when selected contract changes
  console.log("🚀 EditContract: selectedContract", selectedContract);
  // useEffect(() => {
  //   console.log("🚀 EditContract: selectedContract", selectedContract);
  //   if (selectedContract) {
  //     const additionalInfo = selectedContract.additionalInfo || {};

  //     setContractData({
  //       id: selectedContract.id,
  //       consumerId: additionalInfo.consumerid || "",
  //       siteId: additionalInfo.siteId || "",
  //       status: additionalInfo.status === "Active" ? true : false, // Convert string to boolean
  //       startDate: additionalInfo.startdate
  //         ? new Date(additionalInfo.startdate)
  //         : new Date(),
  //       endDate: additionalInfo.enddate
  //         ? new Date(additionalInfo.enddate)
  //         : null,
  //       commodityType: additionalInfo.commodityType
  //         ? {
  //             value: additionalInfo.commodityType,
  //             label: additionalInfo.commodityType,
  //           }
  //         : "",
  //       commodityCategory: additionalInfo.commodityCategory
  //         ? {
  //             value: additionalInfo.commodityCategory,
  //             label: additionalInfo.commodityCategory,
  //           }
  //         : "",
  //       registerRuleBook: additionalInfo.registerRuleBook
  //         ? dropdownOptions.registerRuleBook.find(
  //             (book) => book.value === additionalInfo.registerRuleBook
  //           ) || ""
  //         : "",
  //       subscribedVoltage: additionalInfo.subscribedVoltage || "",
  //       subscribedTariff: additionalInfo.subscribedTariff || "",
  //       billingCycle: additionalInfo.billingCycle || "",
  //       producerPremise: additionalInfo.producerPremise || "",
  //       tariffForm: additionalInfo.tariffForm || undefined,
  //     });
  //   }
  // }, [selectedContract]);

  useEffect(() => {
    if (selectedContract) {
      const mappedData = mapAdditionalInfoToContractData(
        selectedContract,
        dropdownOptions,
      );

      console.log("🚀 EditContract: mappedData", mappedData);

      // Only set if different
      const isDifferent =
        JSON.stringify(contractData) !== JSON.stringify(mappedData);
      if (isDifferent) {
        setContractData(mappedData);
      }
    }
  }, [selectedContract, dropdownOptions]);

  const handleCreateNewContract = () => {
    setCreateNewOne(true);
    // console.log(consumerData, "consumerData");
    // console.log(siteData, "siteData");

    setContractData((old) => ({
      ...old,
      consumerId: consumerData?.id?.id || "",
      siteId: siteData[0]?.id?.id || "",
    }));
  };

  // Auto-select first contract if available

  const defaultContract = useMemo(() => {
    if (
      contractsData &&
      Array.isArray(contractsData) &&
      contractsData.length > 0
    ) {
      return contractsData[0];
    }
    return null;
  }, [contractsData]);

  useEffect(() => {
    if (!selectedContract && defaultContract) {
      setSelectedContract(defaultContract);
    }
  }, [defaultContract]);

  const fetchTaffisData = async () => {
    try {
      const response = await getTariffsV2();
      if (response?.success) {
        const filterData = response.data.map((item) => ({
          label: `${item.name} (v${item.version})`,
          value: item.name,
        }));
        setDropdownOptions((prev) => ({
          ...prev,
          subscribedTariff: filterData,
        }));
      }
    } catch (error) {
      console.error("Error fetching tariffs data:", error);
      setDropdownOptions((prev) => ({
        ...prev,
        subscribedTariff: [],
      }));
    }
  };

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `contract${randomSuffix}`;
    return newName;
  };

  const handleCreateNewOne = async () => {
    // console.log("🚀 AddContract: Starting handleSubmit");
    // console.log(
    //   "📊 Current contractData:",
    //   JSON.stringify(contractData, null, 2)
    // );
    // console.log("📊 Current stepUUIDs:", JSON.stringify(stepUUIDs, null, 2));

    // Add validation for required fields
    if (!contractData?.consumerId) {
      console.error("❌ Consumer ID is missing");
      setToast({
        message: "Consumer ID is required. Please create a consumer first.",
        severity: "error",
      });
      return;
    }

    try {
      const requestData = {
        type: "CONTRACT",
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        label: "NY Building",
        additionalInfo: {
          consumerid: contractData.consumerId,
          siteId: contractData.siteId,
          status: contractData.status ? "Active" : "Inactive", // Store as string
          startdate: contractData.startDate?.toString(),
          enddate: contractData.endDate?.toString(),
          commodityType: contractData.commodityType?.value,
          commodityCategory: contractData.commodityCategory?.value,
          registerRuleBook: contractData.registerRuleBook?.value,
          subscribedVoltage: contractData.subscribedVoltage,
          subscribedTariff: contractData.subscribedTariff?.value,
          billingCycle: contractData.billingCycle,
          producerPremise: contractData.producerPremise,
        },
        customer_id: contractData.consumerId,
        asset_profile_id: "",
        external_id: "",
        version: 0,
      };

      // console.log(
      //   "📤 Sending contract creation request:",
      //   JSON.stringify(requestData, null, 2)
      // );

      let secondResponse = null;
      const response = await fetchPOSTRequest(`asset`, requestData);
      // console.log("📥 Contract creation response:", response);

      if (response?.id?.id) {
        // console.log(
        //   "✅ Contract created successfully with ID:",
        //   response.id.id
        // );

        // Link contract to customer
        // console.log(
        //   `📞 Making customer/asset link API call: customer/${contractData.consumerId}/asset/${response.id.id}`
        // );
        secondResponse = await fetchPOSTRequest(
          `customer/${contractData.consumerId}/asset/${response.id.id}`,
          {},
          true,
        );
        // console.log("📥 Customer/asset link response:", secondResponse);

        if (secondResponse?.status === 200) {
          // console.log("✅ Contract linked to customer successfully");

          // Update step UUID
          // updateStepUUID("contract", response.id.id);

          // Update consumer with the new contract ID - with validation
          try {
            // console.log("🔄 Updating consumer additionalInfo with contract ID");
            // console.log("🔍 Pre-validation check:");
            // console.log("- consumerId:", contractData.consumerId);
            // console.log("- assetType: contract");
            // console.log("- assetId:", response.id.id);

            // Validate all parameters before calling updateConsumerWithAssetId
            if (
              !contractData.consumerId ||
              contractData.consumerId === "undefined"
            ) {
              throw new Error("Consumer ID is undefined or invalid");
            }
            if (!response.id.id || response.id.id === "undefined") {
              throw new Error("Contract ID is undefined or invalid");
            }

            await updateConsumerWithAssetId(
              contractData.consumerId,
              "contract",
              response.id.id,
            );
            // console.log("✅ Consumer additionalInfo updated with contract ID");
          } catch (updateError) {
            // console.error(
            //   "❌ Error updating consumer with contract ID:",
            //   updateError
            // );
            // console.error("Error details:", {
            //   consumerId: contractData.consumerId,
            //   contractId: response.id.id,
            //   errorMessage: updateError.message,
            //   errorResponse: updateError.response?.data,
            // });
            setToast({
              message:
                "Contract created and linked but failed to update consumer info. Please try again.",
              severity: "warning",
            });
          }

          // Reset form
          // setSearchValue({ ...searchValue, value: response.id.id });

          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.id.id +
              " " +
              "Contract created and linked successfully!",
          });
          await refreshData();
        } else {
          console.error("❌ Customer/asset link failed:", secondResponse);
          setToast({
            message:
              "Contract created but failed to link to customer. Please try again.",
            severity: "error",
          });
        }
      } else {
        console.error("❌ Contract creation failed - no response ID");
        setToast({
          message: "Contract creation failed - no response ID received",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("❌ Error during contract creation:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMessage = "Error during contract creation";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    }
  };

  useEffect(() => {
    fetchTaffisData();
  }, []);

  const handleEditedSave = async (skipNext?: boolean) => {
    if (!selectedContract) {
      setToast({
        message: "No contract selected for editing",
        severity: "error",
      });
      return;
    }

    try {
      const requestData = {
        ...selectedContract,
        additionalInfo: {
          ...selectedContract.additionalInfo,
          subscribedVoltage: contractData.subscribedVoltage,
          billingCycle: contractData.billingCycle,
          producerPremise: contractData.producerPremise,
          commodityType:
            contractData.commodityType?.value ||
            contractData.commodityType ||
            "",
          commodityCategory:
            contractData.commodityCategory?.value ||
            contractData.commodityCategory ||
            "",
          registerRuleBook:
            contractData.registerRuleBook?.value ||
            contractData.registerRuleBook ||
            "",
          subscribedTariff:
            contractData.subscribedTariff?.value ||
            contractData.subscribedTariff ||
            "",
        },
      };

      const response = await fetchPOSTRequest(`asset`, requestData, true);

      if (response?.status === 200) {
        if (consumerData?.id?.id && selectedContract?.id?.id) {
          try {
            await updateConsumerWithAssetId(
              consumerData.id.id,
              "contract",
              selectedContract.id.id,
            );
            await refreshData();
          } catch (updateError) {
            console.error(
              "❌ Error updating consumer relationship:",
              updateError,
            );
            setToast({
              message:
                "Contract updated but failed to update consumer relationship. Please refresh the page.",
              severity: "warning",
            });
          }
        }

        showStatus("success", "Contract updated successfully!");

        if (!skipNext) {
          next();
        }
      } else {
        setToast({
          message: response?.data?.message || "Error updating contract",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error updating contract:", error);
      showStatus("error", "Error updating contract");
    }
  };

  const handleSubmit = (skipNext?: boolean) => {
    if (createNewOne) {
      // handleCreateNewOne(skipNext); // If handleCreateNewOne is implemented
    } else {
      handleEditedSave(skipNext);
    }
  };

  // Expose save function to global scope
  useEffect(() => {
    window.handleSubmitContractEdit = handleSubmit;
    return () => {
      delete window.handleSubmitContractEdit;
    };
  }, [selectedContract, contractData]);

  const searchBillings = async () => {
    try {
      const allAssets = [];
      let page = 0;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await fetchGETRequest(
          `tenant/assets?page=${page}&pageSize=10`,
          {},
          true,
        );

        if (response?.status === 200) {
          const assets = response?.data?.data;

          if (assets && Array.isArray(assets)) {
            allAssets.push(...assets);
            hasMorePages = assets.length > 0;
            page += 1;
          } else {
            hasMorePages = false;
          }
        } else {
          hasMorePages = false;
        }
      }

      const filteredAssets = allAssets.filter(
        (asset) =>
          asset.type === "billDeterminant" &&
          contractData.id.id === asset.additionalInfo.contractid &&
          new Date(asset.additionalInfo?.billPeriodFrom) >=
            new Date(billingData.fromDate) &&
          new Date(asset.additionalInfo?.billPeriodTo) <=
            new Date(billingData.toDate),
      );

      setFilteredAssets(filteredAssets);
    } catch (error: any) {
      console.error("Error fetching assets:", error.message);
    }
  };

  const handleGoToBillDeterminant = (billDeterminantId: string) => {
    navigate("/billing-determinant", { state: { billDeterminantId } });
  };

  const dynamicTypeSpecificFields = useMemo(() => {
    const fields = getTypeSpecificConsumerFields("contract");
    const enrichedFields = fields.map((field) => {
      if (field.type === "select") {
        if (dropdownOptions[field.name as keyof typeof dropdownOptions]) {
          return {
            ...field,
            options:
              dropdownOptions[field.name as keyof typeof dropdownOptions],
          };
        }
      }
      return field;
    });

    return enrichedFields;
  }, [dropdownOptions]);

  const memoizedRenderField = useCallback(
    (field: FormField) =>
      renderTypeSpecificField(field, contractData, setContractData),
    [contractData, setContractData],
  );

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={contractData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setContractData({
                      ...contractData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="text"
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Number":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="number"
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Dropdown":
        return (
          <CustomOutlinedSelect
            label={item?.title}
            placeholder={item?.title}
            options={item?.options}
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      default:
        return null;
    }
  };

  if (
    !contractsData ||
    !Array.isArray(contractsData) ||
    (contractsData.length === 0 && createNewOne === false)
  ) {
    return (
      <Box sx={{ p: 4 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <ContainedButton
            label="Create Contract"
            onClick={handleCreateNewContract}
          />
        </Stack>
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No contracts found for this consumer
          </Typography>
        </Box>
      </Box>
    );
  }

  if (createNewOne) {
    return (
      <Box>
        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setActiveTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item) => {
                  return (
                    <Tab
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  );
                })}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Contract", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Grid container spacing={2} sx={{ mt: "10px" }}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Consumer ID
                    </Typography>
                    <CustomSearch
                      placeholder={"Consumer ID"}
                      value={contractData?.consumerId}
                      disabled={true}
                      sx={{
                        width: "100%",
                        height: "36px",
                        borderRadius: "8px",
                        border: "1px solid #c3c3c3",
                        backgroundColor: "#f5f5f5",
                        "& .custom_search_input": {
                          padding: "4px 10px",
                          color: "#666",
                          fontSize: "14px",
                        },
                        "& .MuiInputBase-input": {
                          color: "#666",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Site ID
                    </Typography>
                    <CustomSearch
                      placeholder={"Site ID"}
                      value={contractData?.siteId}
                      disabled={true}
                      sx={{
                        width: "100%",
                        height: "36px",
                        borderRadius: "8px",
                        border: "1px solid #c3c3c3",
                        backgroundColor: "#f5f5f5",
                        "& .custom_search_input": {
                          padding: "4px 10px",
                          color: "#666",
                          fontSize: "14px",
                        },
                        "& .MuiInputBase-input": {
                          color: "#666",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Stack mt={"30px"}>
                      <CustomSwitch
                        label={contractData?.status ? "Active" : "Inactive"}
                        value={contractData?.status}
                        onChange={(e: any) => {
                          setContractData({
                            ...contractData,
                            status: e.target.checked,
                          });
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: "20px" }}>
                  {dynamicTypeSpecificFields
                    .filter(
                      (field) =>
                        field.name !== "status" &&
                        field.name !== "consumerId" &&
                        field.name !== "siteId",
                    )
                    .map((field) => (
                      <Grid
                        key={field.name}
                        size={
                          field.fullWidth
                            ? { xs: 12 }
                            : { xs: 12, md: 4, lg: 4 }
                        }
                        mt={"10px"}
                      >
                        {renderTypeSpecificField(
                          field,
                          contractData,
                          setContractData,
                        )}
                      </Grid>
                    ))}
                </Grid>

                {createdFields?.["Contract"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Contract"]?.map((item: any) => {
                        return (
                          <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                            {getCustomFields(item)}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                >
                  <ContainedButton
                    label={"Add Billing Determinant"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={() => navigate("/billing-determinant")}
                  />
                </Stack>

                <Grid container spacing={2} mt={"20px"}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"From Date:"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"fromDate"}
                      value={billingData?.fromDate || ""}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          fromDate: newValue || new Date(),
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"To Date:"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"toDate"}
                      value={billingData?.toDate || ""}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          toDate: newValue || new Date(),
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      placeholder={"Status"}
                      options={determinantStatusOptions}
                      name={"billingStatus"}
                      value={billingData?.billingStatus}
                      label={"Billing Determinant Status"}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          billingStatus: (newValue?.value as string) || "",
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                <ContainedButton
                  label={"Search"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    mt: "20px",
                    color: "#ffffff",
                  }}
                  onClick={searchBillings}
                />

                <Grid container spacing={2} mt={"20px"}>
                  {filteredAssets
                    .filter(
                      (asset) =>
                        asset.additionalInfo?.contractid ===
                        contractData?.id?.id,
                    )
                    .map((item) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                          <Box
                            sx={{
                              background: "#e3f1fa",
                              mt: "20px",
                              padding: "15px",
                              borderRadius: "6px",
                            }}
                          >
                            <Stack
                              direction={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={"400"}
                                fontSize={"13px"}
                              >
                                Bill Determinant ID:
                              </Typography>
                              <Typography variant="body2" fontWeight={"600"}>
                                {item?.id?.id}
                              </Typography>
                            </Stack>

                            <Typography
                              variant="body1"
                              mt={"5px"}
                              fontSize={"13px"}
                              fontWeight={"400"}
                            >
                              {" "}
                              From: {item?.additionalInfo?.billPeriodFrom}
                            </Typography>
                            <Typography
                              variant="body1"
                              mt={"5px"}
                              fontSize={"13px"}
                              fontWeight={"400"}
                            >
                              {" "}
                              To: {item?.additionalInfo?.billPeriodTo}
                            </Typography>

                            <Stack
                              direction={"row"}
                              justifyContent={"flex-end"}
                            >
                              <ContainedButton
                                label={"Go To Bill Determinant"}
                                sx={{
                                  width: "fit-content",
                                  fontWeight: 600,
                                  mt: "10px",
                                  color: "#ffffff",
                                }}
                                onClick={() =>
                                  handleGoToBillDeterminant(item?.id?.id)
                                }
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </TabPanel>

              {/* <Stack direction={"direction"} justifyContent={"space-between"} justifyItems={"center"} mt={"20px"}>

              <ContainedButton
                label={"Go to Consumer"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => handleGoToConsumer(contractData?.consumerId)}
              />

              <ContainedButton
                label={"Go to Site"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={()=> handleGoToSite(contractData?.siteId)}
              />

            </Stack> */}
            </CardContent>
          </TabContext>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Contract Selection */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Select Contract to Edit
        </Typography>
        <Grid container spacing={2}>
          {contractsData.map((contract, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={contract.id.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  border:
                    selectedContract?.id.id === contract.id.id
                      ? "2px solid #1976d2"
                      : "1px solid #e0e0e0",
                  "&:hover": {
                    boxShadow: 3,
                  },
                }}
                onClick={() => setSelectedContract(contract)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contract {contract.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {contract.id.id}
                  </Typography>
                  <Typography variant="body2">
                    Type: {contract.additionalInfo?.commodityType || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Start Date:{" "}
                    {contract.additionalInfo?.startdate
                      ? formatDate(
                          new Date(contract.additionalInfo.startdate),
                          "DD/MM/YYYY",
                        )
                      : "N/A"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contract Edit Form */}
      {selectedContract && (
        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setActiveTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "#1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item) => (
                  <Tab
                    key={item}
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                ))}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Contract", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Editing Contract: {selectedContract.name}
                  </Typography>
                  {/* <CustomSwitch
                    label={contractData?.status ? "Active" : "Inactive"}
                    value={contractData?.status}
                    onChange={(e) => {
                      setContractData({
                        ...contractData,
                        status: e.target.checked,
                      });
                    }}
                  /> */}
                </Stack>

                <Grid container spacing={2} sx={{ mt: "10px" }}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Consumer ID
                    </Typography>
                    <CustomSearch
                      placeholder={"Consumer ID"}
                      value={contractData?.consumerId}
                      disabled={true}
                      sx={{
                        width: "100%",
                        height: "36px",
                        borderRadius: "8px",
                        border: "1px solid #c3c3c3",
                        backgroundColor: "#f5f5f5",
                        "& .custom_search_input": {
                          padding: "4px 10px",
                          color: "#666",
                          fontSize: "14px",
                        },
                        "& .MuiInputBase-input": {
                          color: "#666",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Site ID
                    </Typography>
                    <CustomSearch
                      placeholder={"Site ID"}
                      value={contractData?.siteId}
                      disabled={true}
                      sx={{
                        width: "100%",
                        height: "36px",
                        borderRadius: "8px",
                        border: "1px solid #c3c3c3",
                        backgroundColor: "#f5f5f5",
                        "& .custom_search_input": {
                          padding: "4px 10px",
                          color: "#666",
                          fontSize: "14px",
                        },
                        "& .MuiInputBase-input": {
                          color: "#666",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Stack mt={"30px"}>
                      <CustomSwitch
                        label={contractData?.status ? "Active" : "Inactive"}
                        value={contractData?.status}
                        onChange={(e: any) => {
                          setContractData({
                            ...contractData,
                            status: e.target.checked,
                          });
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: "20px" }}>
                  {dynamicTypeSpecificFields
                    .filter(
                      (field) =>
                        field.name !== "status" &&
                        field.name !== "consumerId" &&
                        field.name !== "siteId",
                    )
                    .map((field) => (
                      <Grid
                        key={field.name}
                        size={{ xs: 12, md: 4, lg: 4 }}
                        mt={"10px"}
                      >
                        {memoizedRenderField(field)}
                      </Grid>
                    ))}
                </Grid>

                {createdFields?.["Contract"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Contract"]?.map(
                        (item: any, index: number) => (
                          <Grid
                            size={{ xs: 12, md: 4, lg: 4 }}
                            mt={"10px"}
                            key={index}
                          >
                            {getCustomFields(item)}
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Grid container spacing={2} mt={"20px"}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"From Date"}
                      placeholder={"dd/mm/yyyy"}
                      value={billingData?.fromDate}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          fromDate: newValue as Date,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"To Date"}
                      placeholder={"dd/mm/yyyy"}
                      value={billingData?.toDate}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          toDate: newValue as Date,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      placeholder={"Status"}
                      options={determinantStatusOptions}
                      value={billingData?.billingStatus}
                      label={"Billing Determinant Status"}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          billingStatus: newValue?.value as string,
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                <ContainedButton
                  label={"Search"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    mt: "20px",
                    color: "#ffffff",
                  }}
                  onClick={searchBillings}
                />

                <Grid container spacing={2} mt={"20px"}>
                  {filteredAssets
                    .filter(
                      (asset) =>
                        asset.additionalInfo?.contractid ===
                        contractData?.id?.id,
                    )
                    .map((item, index) => (
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} key={index}>
                        <Box
                          sx={{
                            background: "#e3f1fa",
                            mt: "20px",
                            padding: "15px",
                            borderRadius: "6px",
                          }}
                        >
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                          >
                            <Typography
                              variant="body1"
                              fontWeight={"400"}
                              fontSize={"13px"}
                            >
                              Bill Determinant ID:
                            </Typography>
                            <Typography variant="body2" fontWeight={"600"}>
                              {item?.id?.id}
                            </Typography>
                          </Stack>

                          <Typography
                            variant="body1"
                            mt={"5px"}
                            fontSize={"13px"}
                            fontWeight={"400"}
                          >
                            From: {item?.additionalInfo?.billPeriodFrom}
                          </Typography>
                          <Typography
                            variant="body1"
                            mt={"5px"}
                            fontSize={"13px"}
                            fontWeight={"400"}
                          >
                            To: {item?.additionalInfo?.billPeriodTo}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              </TabPanel>
            </CardContent>
          </TabContext>
        </Card>
      )}
    </Box>
  );
};

interface EditMeterProps {
  consumerData: any;
  metersData: any;
  sitesData: any;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  createOnlyMeter: boolean;
  activeStep: number;
  next: () => void;
  back: () => void;
  onDataUpdate: (data: any) => void;
  refreshData: () => Promise<void>;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
    action?: "add" | "remove",
  ) => Promise<void>;
}

const EditMeter = ({
  consumerData,
  metersData,
  sitesData,
  setToast,
  showStatus,
  createOnlyMeter,
  activeStep,
  next,
  back,
  onDataUpdate,
  refreshData,
  updateConsumerWithAssetId,
}: EditMeterProps): React.JSX.Element => {
  const [selectedMeter, setSelectedMeter] = useState<any>(null);
  const [assignExistingMeter, setAssignExistingMeter] = useState(false);
  const [createNewMeter, setCreateNewMeter] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Meters");

  const [meterInfo, setMeterInfo] = useState<any>({
    connectionStatus: true,
    useAsLoadProfile: false,
    meterType: "",
    manufacturer: "",
    meterSerialNo: "",
    meterBadgeNo: "",
    meterInstallDate: null,
    status: "Active",
    siteId: "",
    existingMeterNumber: "",
    lineLtCtRatio: "",
    meterBoxDimensions: "",
    meterLegalStatus: "",
    remarks: "",
    oldCustomerId: "",
  });

  const [readClusterDetail, setReadClusterDetail] = useState<{
    startDate: Date;
    endDate: Date;
    registerTypeCode: { label: string; value: string };
  }>({
    startDate: new Date(),
    endDate: new Date(),
    registerTypeCode: { label: "KWH", value: "IP_KWH" },
  });

  const [dynamicTypeSpecificFields, setDynamicTypeSpecificFields] = useState<
    any[]
  >([]);

  const [dropdownOptions, setDropdownOptions] = useState<any>({
    meterType: [
      { label: "SES-RES-SERIES", value: "SES-RES-SERIES" },
      { label: "SES-COM-SERIES", value: "SES-COM-SERIES" },
      { label: "SES-RES-INTERVAL", value: "SES-RES-INTERVAL" },
      { label: "SES-COM-INTERVAL", value: "SES-COM-INTERVAL" },
      { label: "ITR-RES-SERIES", value: "ITR-RES-SERIES" },
      { label: "ITR-COM-SERIES", value: "ITR-COM-SERIES" },
      { label: "ITR-RES-INTERVAL", value: "ITR-RES-INTERVAL" },
      { label: "ITR-COM-INTERVAL", value: "ITR-COM-INTERVAL" },
    ],
    manufacturer: [
      { value: "SENSUS", label: "SENSUS" },
      { value: "ITRON", label: "ITRON" },
    ],
    assignToCustomer: [],
    sites: [],
  });

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();
  const TABS = ["Meters", "Read Cluster"];

  // Load meter data when selected meter changes
  useEffect(() => {
    if (selectedMeter && !assignExistingMeter && !createNewMeter) {
      const additionalInfo = selectedMeter.additionalInfo || {};
      setMeterInfo({
        connectionStatus: additionalInfo.connectionStatus === "Connected",
        useAsLoadProfile: !!additionalInfo.UseAsLoadProfile,
        meterType: additionalInfo.meterCommodityType
          ? {
              value: additionalInfo.meterCommodityType,
              label: additionalInfo.meterCommodityType,
            }
          : "",
        manufacturer: additionalInfo.manufacturer
          ? {
              value: additionalInfo.manufacturer,
              label: additionalInfo.manufacturer,
            }
          : "",
        meterSerialNo: additionalInfo.SerialNumber || "",
        meterBadgeNo: additionalInfo.BadgeNumber || "",
        meterInstallDate: additionalInfo.InstallationDate
          ? new Date(additionalInfo.InstallationDate)
          : null,
        status: additionalInfo.status || "Active",
        siteId: additionalInfo.siteId || "",
        existingMeterNumber: selectedMeter.id?.id || "",
        lineLtCtRatio: additionalInfo.lineLtCtRatio || "",
        meterBoxDimensions: additionalInfo.meterBoxDimensions || "",
        meterLegalStatus: additionalInfo.meterLegalStatus || "",
        remarks: additionalInfo.remarks || "",
        oldCustomerId: selectedMeter.customerId?.id || "",
      });

      if (additionalInfo.meterCommodityType) {
        handleMeterTypeChange({
          value: additionalInfo.meterCommodityType,
          label: additionalInfo.meterCommodityType,
        });
      }
    }
  }, [selectedMeter]);

  // Transform sites data for dropdown
  useEffect(() => {
    if (sitesData) {
      const transformedSites = sitesData.map((site: any) => ({
        label: site.name || site.id?.id,
        value: site.id?.id,
      }));
      setDropdownOptions((prev: any) => ({
        ...prev,
        sites: transformedSites,
      }));
    }
  }, [sitesData]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetchGETRequest(
        `customers?pageSize=10&page=0`,
        {},
        true,
      );
      const transformed = response?.data?.map((c: any) => ({
        label: c.name,
        value: c.id.id,
      }));
      setDropdownOptions((prev: any) => ({
        ...prev,
        assignToCustomer: transformed || [],
      }));
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Auto-select first meter or initialize for new meter
  useEffect(() => {
    if (
      metersData?.length > 0 &&
      !selectedMeter &&
      !createNewMeter &&
      !assignExistingMeter
    ) {
      setSelectedMeter(metersData[0]);
    } else if (createNewMeter && !meterInfo.siteId && sitesData?.length > 0) {
      // Default to first site if creating new meter and no site selected
      setMeterInfo((prev: any) => ({
        ...prev,
        siteId: sitesData[0].id?.id,
      }));
    }
  }, [metersData, createNewMeter, assignExistingMeter, sitesData]);

  const handleMeterTypeChange = async (selectedMeterType: any) => {
    setMeterInfo((prev: any) => ({
      ...prev,
      meterType: selectedMeterType,
    }));

    if (selectedMeterType?.value) {
      const typeSpecificFields = getTypeSpecificFields(
        selectedMeterType.value.toLowerCase(),
      );
      setDynamicTypeSpecificFields(typeSpecificFields);
    } else {
      setDynamicTypeSpecificFields([]);
    }
  };

  const fetchMeterDetails = async (payload?: string) => {
    const searchValueToUse = payload || meterInfo.existingMeterNumber;
    if (!searchValueToUse?.trim()) {
      setToast({
        message: "Please enter a valid Meter ID.",
        severity: "error",
      });
      return;
    }

    if (window.setIsLoading) window.setIsLoading(true);

    try {
      const response = await fetchGETRequest(
        `device/${searchValueToUse.trim()}`,
        {},
        true,
      );
      if (response?.data) {
        const additionalInfo = response.data.additionalInfo || {};
        setMeterInfo((prev: any) => ({
          ...prev,
          meterType: additionalInfo.meterCommodityType
            ? {
                value: additionalInfo.meterCommodityType,
                label: additionalInfo.meterCommodityType,
              }
            : "",
          manufacturer: additionalInfo.manufacturer
            ? {
                value: additionalInfo.manufacturer,
                label: additionalInfo.manufacturer,
              }
            : "",
          meterSerialNo: additionalInfo.SerialNumber || "",
          meterBadgeNo: additionalInfo.BadgeNumber || "",
          meterInstallDate: additionalInfo.InstallationDate
            ? new Date(additionalInfo.InstallationDate)
            : null,
          connectionStatus: additionalInfo.connectionStatus === "Connected",
          status: additionalInfo.status || "Active",
          siteId: additionalInfo.siteId || "",
          oldCustomerId: response.data.customerId?.id || "",
        }));

        if (additionalInfo.meterCommodityType) {
          handleMeterTypeChange({
            value: additionalInfo.meterCommodityType,
            label: additionalInfo.meterCommodityType,
          });
        }
      } else {
        setToast({ message: "Meter not found.", severity: "warning" });
      }
    } catch (error) {
      console.error("Error fetching meter details:", error);
      setToast({ message: "Error fetching meter details", severity: "error" });
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  const handleSave = async (skipNext?: boolean) => {
    if (createNewMeter) {
      await handleCreateNewMeter(skipNext);
      return;
    }

    if (assignExistingMeter) {
      await handleAssignMeter(skipNext);
      return;
    }

    if (!selectedMeter) {
      setToast({ message: "No meter selected for editing", severity: "error" });
      return;
    }

    // Existing edit logic
    try {
      if (window.setIsLoading) window.setIsLoading(true);
      const requestData = {
        ...selectedMeter,
        additionalInfo: {
          ...selectedMeter.additionalInfo,
          connectionStatus: meterInfo.connectionStatus
            ? "Connected"
            : "Disconnected",
          meterCommodityType: meterInfo.meterType?.value || "",
          manufacturer: meterInfo.manufacturer?.value || "",
          SerialNumber: meterInfo.meterSerialNo || "",
          BadgeNumber: meterInfo.meterBadgeNo || "",
          InstallationDate: meterInfo.meterInstallDate?.toString(),
          status: meterInfo.status || "Active",
          siteId: meterInfo.siteId || "",
          ...dynamicTypeSpecificFields.reduce((acc, field) => {
            const val = meterInfo[field.name];
            if (val !== undefined) acc[field.name] = val?.value || val;
            return acc;
          }, {}),
        },
      };

      const response = await fetchPOSTRequest(`device`, requestData, true);
      if (response?.status === 200) {
        showStatus("success", "Meter updated successfully!");
        await refreshData();
        if (!skipNext) next();
      }
    } catch (error) {
      console.error("Error updating meter:", error);
      showStatus("error", "Error updating meter");
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  // Expose save function to global scope
  useEffect(() => {
    window.handleSaveMeterEdit = handleSave;
    return () => {
      delete window.handleSaveMeterEdit;
    };
  }, [handleSave]);

  const handleAssignMeter = async (skipNext?: boolean) => {
    if (!meterInfo.existingMeterNumber) {
      setToast({
        message: "Please enter an Existing Meter ID",
        severity: "warning",
      });
      return;
    }

    try {
      if (window.setIsLoading) window.setIsLoading(true);
      let oldCustomerId = meterInfo.oldCustomerId;
      if (!oldCustomerId) {
        const checkRes = await fetchGETRequest(
          `device/${meterInfo.existingMeterNumber}`,
          {},
          true,
        );
        oldCustomerId = checkRes?.data?.customerId?.id;
      }

      const linkResponse = await fetchPOSTRequest(
        `customer/${consumerData.id.id}/device/${meterInfo.existingMeterNumber}`,
        {},
        true,
      );

      if (linkResponse?.status === 200) {
        await updateConsumerWithAssetId(
          consumerData.id.id,
          "meter",
          meterInfo.existingMeterNumber,
          "add",
        );
        if (oldCustomerId && oldCustomerId !== consumerData.id.id) {
          await updateConsumerWithAssetId(
            oldCustomerId,
            "meter",
            meterInfo.existingMeterNumber,
            "remove",
          );
        }
        showStatus("success", "Meter reassigned successfully!");
        await refreshData();
        if (!skipNext) next();
      }
    } catch (error) {
      console.error("Error reassigning meter:", error);
      showStatus("error", "Error reassigning meter");
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  const handleCreateNewMeter = async (skipNext?: boolean) => {
    if (window.setIsLoading) window.setIsLoading(true);
    try {
      const requestData = {
        name: `Meter${Math.floor(Math.random() * 1000000000)}`,
        type: "DEVICE",
        additionalInfo: {
          connectionStatus: meterInfo.connectionStatus
            ? "Connected"
            : "Disconnected",
          meterCommodityType: meterInfo.meterType?.value || "",
          manufacturer: meterInfo.manufacturer?.value || "",
          SerialNumber: meterInfo.meterSerialNo || "",
          BadgeNumber: meterInfo.meterBadgeNo || "",
          InstallationDate: meterInfo.meterInstallDate?.toString(),
          status: "Active",
          siteId: meterInfo.siteId?.value || meterInfo.siteId || "",
          ...dynamicTypeSpecificFields.reduce((acc, field) => {
            const val = meterInfo[field.name];
            if (val !== undefined) acc[field.name] = val?.value || val;
            return acc;
          }, {}),
        },
      };

      const response = await fetchPOSTRequest(`device`, requestData, true);
      if (response?.data?.id?.id) {
        const meterId = response.data.id.id;
        const customerIdToLink =
          meterInfo.assignToCustomer?.value || consumerData.id.id;
        await fetchPOSTRequest(
          `customer/${customerIdToLink}/device/${meterId}`,
          {},
          true,
        );
        await updateConsumerWithAssetId(
          customerIdToLink,
          "meter",
          meterId,
          "add",
        );
        showStatus("success", "Meter created successfully!");
        await refreshData();
        if (!skipNext) {
          handleResetMeterInfo();
          next();
        }
      }
    } catch (error) {
      console.error("Error creating meter:", error);
      showStatus("error", "Error creating meter");
    } finally {
      if (window.setIsLoading) window.setIsLoading(false);
    }
  };

  const handleResetMeterInfo = () => {
    setMeterInfo({
      connectionStatus: true,
      useAsLoadProfile: false,
      meterType: "",
      manufacturer: "",
      meterSerialNo: "",
      meterBadgeNo: "",
      meterInstallDate: null,
      status: "Active",
      siteId: "",
      existingMeterNumber: "",
      lineLtCtRatio: "",
      meterBoxDimensions: "",
      meterLegalStatus: "",
      remarks: "",
      oldCustomerId: "",
    });
    setDynamicTypeSpecificFields([]);
  };

  useEffect(() => {
    window.handleSaveMeter = handleSave;
    return () => {
      delete window.handleSaveMeter;
    };
  }, [
    selectedMeter,
    meterInfo,
    dynamicTypeSpecificFields,
    assignExistingMeter,
    createNewMeter,
  ]);

  const removeSelectedMeter = () => {
    setSelectedMeter(null);
    handleResetMeterInfo();
  };

  const dynamicTypeSpecificFieldsArray = useMemo(() => {
    const fields = getTypeSpecificConsumerFields("meter");
    const enrichedFields = fields.map((field) => {
      if (field.type === "select") {
        if (dropdownOptions[field.name as keyof typeof dropdownOptions]) {
          return {
            ...field,
            options:
              dropdownOptions[field.name as keyof typeof dropdownOptions],
          };
        }
      }
      return field;
    });

    return enrichedFields;
  }, [dropdownOptions]);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={meterInfo?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setMeterInfo((old: any) => ({
                ...old,
                [item?.title?.toUpperCase()]: event,
              }));
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={meterInfo?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setMeterInfo((old: any) => ({
                      ...old,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    }));
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="text"
            value={meterInfo?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setMeterInfo((old: any) => ({
                ...old,
                [item?.title?.toUpperCase()]: event.target.value,
              }));
            }}
          />
        );
      case "Number":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="number"
            value={meterInfo?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setMeterInfo((old: any) => ({
                ...old,
                [item?.title?.toUpperCase()]: event.target.value,
              }));
            }}
          />
        );
      case "Dropdown":
        return (
          <CustomOutlinedSelect
            label={item?.title}
            placeholder={item?.title}
            options={item?.options}
            value={meterInfo?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setMeterInfo((old: any) => ({
                ...old,
                [item?.title?.toUpperCase()]: event,
              }));
            }}
          />
        );
      default:
        return null;
    }
  };

  if (
    !metersData ||
    !Array.isArray(metersData) ||
    (metersData.length === 0 && !createNewMeter && !assignExistingMeter)
  ) {
    return (
      <Box sx={{ p: 4 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <ContainedButton
            label="Create Meter"
            onClick={() => setCreateNewMeter(true)}
          />
        </Stack>
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No meters found for this consumer
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Meter Selection */}
      {/* <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Select Meter to Edit
        </Typography>
        <Grid container spacing={2}>
          {metersData.map((meter, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={meter.id.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  border:
                    selectedMeter?.id.id === meter.id.id
                      ? "2px solid #1976d2"
                      : "1px solid #e0e0e0",
                  "&:hover": {
                    boxShadow: 3,
                  },
                }}
                onClick={() => setSelectedMeter(meter)}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Meter {meter.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {meter.id.id}
                  </Typography>
                  <Typography variant="body2">
                    Type: {meter.additionalInfo?.meterCommodityType || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Serial: {meter.additionalInfo?.SerialNumber || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Status: {meter.additionalInfo?.status || "N/A"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box> */}

      {/* Meter Edit Form */}
      {(selectedMeter || createNewMeter || assignExistingMeter) && (
        <Card sx={{ mt: "20px" }}>
          <TabContext value={selectedTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setSelectedTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "#1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((tab) => (
                  <Tab
                    key={tab}
                    label={tab}
                    value={tab}
                    className="consumer_right_card_list_tab"
                  />
                ))}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                  mr: 2,
                }}
                onClick={() => toggleCustomFieldDrawer("Meter", true)}
              />
              {!createNewMeter &&
                !assignExistingMeter &&
                (!metersData || metersData.length === 0) && (
                  <ContainedButton
                    label={"Create New Meter"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={() => {
                      setCreateNewMeter(true);
                      setSelectedMeter(null);
                      setAssignExistingMeter(false);
                      handleResetMeterInfo();
                    }}
                  />
                )}
            </Box>

            <CardContent>
              <TabPanel value={"Meters"} sx={{ padding: 0 }}>
                {/* Assign Existing Meter Section */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  sx={{ mt: "10px", mb: "0px" }}
                >
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={assignExistingMeter}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setAssignExistingMeter(isChecked);
                            setCreateNewMeter(!isChecked);
                            if (isChecked) {
                              setSelectedMeter(null);
                              handleResetMeterInfo();
                            }
                          }}
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                          }}
                        />
                      }
                      label={"Assign Existing Meter"}
                    />
                  </Grid>

                  {assignExistingMeter && (
                    <Grid size={{ xs: 12, md: 8, lg: 8 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <CustomInput
                          label="Existing Meter ID"
                          placeholder="Enter Meter ID"
                          value={meterInfo.existingMeterNumber}
                          onChange={(e) =>
                            setMeterInfo((prev: any) => ({
                              ...prev,
                              existingMeterNumber: e.target.value,
                            }))
                          }
                        />
                        <ContainedButton
                          label="Search"
                          onClick={() => fetchMeterDetails()}
                          sx={{ height: "40px", mb: "4px" }}
                        />
                      </Stack>
                    </Grid>
                  )}
                </Grid>

                {(assignExistingMeter || createNewMeter) && (
                  <Box
                    sx={{
                      mt: 3,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {createNewMeter
                        ? "Meter Details"
                        : "Meter Details (from Search)"}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomOutlinedSelect
                          label={"Meter Type"}
                          placeholder={"Select Meter Type"}
                          options={dropdownOptions.meterType}
                          value={meterInfo?.meterType}
                          onChange={handleMeterTypeChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomOutlinedSelect
                          label={"Manufacturer"}
                          placeholder={"Select Manufacturer"}
                          options={dropdownOptions.manufacturer}
                          value={meterInfo?.manufacturer}
                          onChange={(e: any) =>
                            setMeterInfo({ ...meterInfo, manufacturer: e })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomOutlinedSelect
                          label={"Site"}
                          placeholder={"Select Site"}
                          options={dropdownOptions.sites}
                          value={dropdownOptions.sites?.find(
                            (s: any) =>
                              s.value ===
                              (meterInfo.siteId?.value || meterInfo.siteId),
                          )}
                          onChange={(e: any) =>
                            setMeterInfo({ ...meterInfo, siteId: e })
                          }
                        />
                      </Grid>
                      {createNewMeter && (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          <CustomOutlinedSelect
                            label={"Assign to Customer"}
                            placeholder={"Select Customer"}
                            options={dropdownOptions.assignToCustomer}
                            value={dropdownOptions.assignToCustomer?.find(
                              (c: any) =>
                                c.value ===
                                (meterInfo.assignToCustomer?.value ||
                                  meterInfo.assignToCustomer ||
                                  consumerData.id.id),
                            )}
                            onChange={(e: any) =>
                              setMeterInfo({
                                ...meterInfo,
                                assignToCustomer: e,
                              })
                            }
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomInput
                          label={"Serial Number"}
                          placeholder={"Enter Serial Number"}
                          value={meterInfo.meterSerialNo}
                          onChange={(e) =>
                            setMeterInfo({
                              ...meterInfo,
                              meterSerialNo: e.target.value,
                            })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomInput
                          label={"Badge Number"}
                          placeholder={"Enter Badge Number"}
                          value={meterInfo.meterBadgeNo}
                          onChange={(e) =>
                            setMeterInfo({
                              ...meterInfo,
                              meterBadgeNo: e.target.value,
                            })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <DatePickerInput
                          label={"Install Date"}
                          placeholder={"dd/mm/yyyy"}
                          value={meterInfo.meterInstallDate}
                          onChange={(e) =>
                            setMeterInfo({ ...meterInfo, meterInstallDate: e })
                          }
                        />
                      </Grid>
                      {/* <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <Stack mt={"30px"}>
                          <CustomSwitch
                            label={
                              meterInfo?.connectionStatus
                                ? "Connected"
                                : "Disconnected"
                            }
                            value={meterInfo?.connectionStatus}
                            onChange={(e) =>
                              setMeterInfo({
                                ...meterInfo,
                                connectionStatus: e.target.checked,
                              })
                            }
                          />
                        </Stack>
                      </Grid> */}
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {dynamicTypeSpecificFields.map((field) => (
                        <Grid
                          key={field.name}
                          size={{ xs: 12, md: 4 }}
                          mt={"10px"}
                        >
                          {renderTypeSpecificField(
                            field,
                            meterInfo,
                            setMeterInfo,
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Only show selected meter edit if neither assign nor create is active */}
                {!assignExistingMeter && !createNewMeter && selectedMeter && (
                  <>
                    <Grid container spacing={2} sx={{ mt: "10px" }}>
                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <Stack mt={"30px"}>
                          <CustomSwitch
                            label={
                              meterInfo?.connectionStatus
                                ? "Connected"
                                : "Disconnected"
                            }
                            value={meterInfo?.connectionStatus}
                            onChange={(e: any) =>
                              setMeterInfo({
                                ...meterInfo,
                                connectionStatus: e.target.checked,
                              })
                            }
                          />
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <Stack mt={"30px"}>
                          <CustomSwitch
                            label={"Use As Load Profile"}
                            value={meterInfo?.useAsLoadProfile}
                            onChange={(e: any) =>
                              setMeterInfo({
                                ...meterInfo,
                                useAsLoadProfile: e.target.checked,
                              })
                            }
                          />
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                        <CustomOutlinedSelect
                          label={"Site"}
                          placeholder={"Select Site"}
                          options={dropdownOptions.sites}
                          value={dropdownOptions.sites?.find(
                            (s: any) =>
                              s.value ===
                              (meterInfo.siteId?.value || meterInfo.siteId),
                          )}
                          onChange={(e: any) =>
                            setMeterInfo({ ...meterInfo, siteId: e })
                          }
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: "20px" }}>
                      {dynamicTypeSpecificFieldsArray
                        .filter(
                          (field) =>
                            field.name !== "connectionStatus" &&
                            field.name !== "useAsLoadProfile",
                        )
                        .map((field) => (
                          <Grid
                            key={field.name}
                            size={
                              field.fullWidth
                                ? { xs: 12 }
                                : { xs: 12, md: 4, lg: 4 }
                            }
                            mt={"10px"}
                          >
                            {renderTypeSpecificField(
                              field,
                              meterInfo,
                              setMeterInfo,
                            )}
                          </Grid>
                        ))}
                    </Grid>

                    {createdFields?.["Meter"] && (
                      <>
                        <Stack
                          sx={{ paddingTop: "30px", paddingBottom: "20px" }}
                        >
                          <Divider sx={{ borderWidth: "1.5px" }} />
                        </Stack>
                        <Grid container spacing={2}>
                          {createdFields?.["Meter"]?.map(
                            (item: any, index: number) => (
                              <Grid
                                size={{ xs: 12, md: 4, lg: 4 }}
                                mt={"10px"}
                                key={index}
                              >
                                {getCustomFields(item)}
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </TabPanel>

              <TabPanel value={"Read Cluster"} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"Start Date"}
                      placeholder={"dd/mm/yyyy"}
                      value={readClusterDetail?.startDate}
                      onChange={(event) => {
                        setReadClusterDetail({
                          ...readClusterDetail,
                          startDate: event as Date,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"End Date"}
                      value={readClusterDetail?.endDate}
                      placeholder={"dd/mm/yyyy"}
                      onChange={(event) => {
                        setReadClusterDetail({
                          ...readClusterDetail,
                          endDate: event as Date,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      label={"Register Type Code"}
                      options={dropdownOptions.meterType}
                      placeholder={"Enter or Select a Register Type"}
                      value={readClusterDetail.registerTypeCode}
                      onChange={(event) => {
                        setReadClusterDetail({
                          ...readClusterDetail,
                          registerTypeCode: {
                            value: (event?.value as string) || "",
                            label: (event?.label as string) || "",
                          },
                        });
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </CardContent>
          </TabContext>
        </Card>
      )}
    </Box>
  );
};

export default EditConsumerModel;
